// API Client for DealHub
// Communicates with Cloudflare Worker backend

import type {
  Deal,
  CreateDealInput,
  Quote,
  Scope,
  RoiCalculation,
  Deliverable,
  DealWithRelations,
  ApiResponse,
  PaginatedResponse,
  ScopeHour,
  RoiInputs,
  RoiOutputs,
  DeliverableStatus,
  Recipe,
  Draft,
  PresalesDeal,
  PresalesKR1Result,
  PresalesKR2Result,
  KR1RollingResult,
  KR2RollingResult,
  KR1Deal,
  KR2DealsResponse,
  PresalesKR3Result,
  KR3Demo,
  PresalesKR4Result,
  PresalesSyncResult,
  CalendarEventsResponse,
  EventLinkResult,
  EventLinkSummary,
  ProspectSummary,
  UnlinkResult,
  ReviewSuggestionResult,
  GoalState,
  SentimentEntry,
  PresalesTask,
  PresalesTaskAuditEntry,
  TaskAnalyticsResult,
  HoursAnalyticsResult,
  JiraSearchResponse,
  WaitingJiraTask,
  AuditLogEntry,
  DurationOverride,
  ImplTimeEntry,
  JiraWorklogEntry,
  SlaScanResult,
} from './types'
import type { Product } from '../features/quote/types'

export interface ImplTodoItem {
  id: number
  category_id: number
  title: string
  detail: string | null
  target_date: string | null
  status: 'todo' | 'in-progress' | 'done'
  sort_order: number
  created_at: string
}

export interface ImplTodoCategory {
  id: number
  title: string
  sort_order: number
  created_at: string
  items: ImplTodoItem[]
}

// Base URL - will be configured for production
const API_BASE = import.meta.env.VITE_API_URL || '/api'
console.log('API_BASE:', API_BASE, 'from env:', import.meta.env.VITE_API_URL)

// =====================================================
// Base Request Helper
// =====================================================
interface RequestOptions extends RequestInit {
  params?: Record<string, string>
}

// Get auth headers for API requests
export function getAuthHeaders(): Record<string, string> {
  try {
    const stored = localStorage.getItem('frankieone-auth')
    if (stored) {
      const parsed = JSON.parse(stored)
      const idToken = parsed?.state?.idToken
      if (idToken) return { 'Authorization': `Bearer ${idToken}` }
      // Dev mode fallback — no token, send dev header
      if (parsed?.state?.devMode) return { 'X-Dev-User-Email': 'dev@frankieone.com' }
    }
  } catch { /* ignore */ }
  return {}
}

// Attempt silent Google token refresh via One Tap prompt.
// Returns the new ID token on success, or null if refresh fails.
const GOOGLE_CLIENT_ID = '759138193168-4p3hjiuv841k1qnsialtf1o5suqoeian.apps.googleusercontent.com'
let _refreshPromise: Promise<string | null> | null = null

function refreshGoogleToken(): Promise<string | null> {
  // Deduplicate concurrent refresh attempts
  if (_refreshPromise) return _refreshPromise
  _refreshPromise = new Promise<string | null>((resolve) => {
    const google = (window as any).google
    if (!google?.accounts?.id) {
      resolve(null)
      return
    }
    // Set a timeout — if prompt doesn't fire callback within 5s, give up
    const timer = setTimeout(() => resolve(null), 5000)
    google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: (response: { credential: string }) => {
        clearTimeout(timer)
        // Store the new token
        try {
          const stored = localStorage.getItem('frankieone-auth')
          if (stored) {
            const parsed = JSON.parse(stored)
            if (parsed?.state) {
              parsed.state.idToken = response.credential
              localStorage.setItem('frankieone-auth', JSON.stringify(parsed))
            }
          }
        } catch { /* ignore */ }
        resolve(response.credential)
      },
      auto_select: true,
    })
    google.accounts.id.prompt((notification: any) => {
      // If prompt was dismissed or not displayed, resolve null
      if (notification.isNotDisplayed() || notification.isDismissedMoment()) {
        clearTimeout(timer)
        resolve(null)
      }
    })
  }).finally(() => { _refreshPromise = null })
  return _refreshPromise
}

async function apiRequest<T>(
  endpoint: string,
  options: RequestOptions = {},
  _isRetry = false
): Promise<ApiResponse<T>> {
  try {
    const { params, ...fetchOptions } = options

    let url = `${API_BASE}${endpoint}`
    if (params) {
      const searchParams = new URLSearchParams(params)
      url += `?${searchParams.toString()}`
    }

    const response = await fetch(url, {
      ...fetchOptions,
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
        ...fetchOptions.headers,
      },
    })

    // Handle 401 — attempt silent token refresh, then retry once
    if (response.status === 401) {
      if (!_isRetry) {
        const newToken = await refreshGoogleToken()
        if (newToken) {
          return apiRequest<T>(endpoint, options, true)
        }
      }
      // Refresh failed or already retried — redirect to login
      try {
        const stored = localStorage.getItem('frankieone-auth')
        if (stored) {
          const parsed = JSON.parse(stored)
          if (parsed?.state) {
            parsed.state.user = null
            parsed.state.isAuthenticated = false
            parsed.state.idToken = null
            parsed.state.devMode = false
            localStorage.setItem('frankieone-auth', JSON.stringify(parsed))
          }
        }
      } catch { /* ignore */ }
      const returnPath = window.location.pathname + window.location.search; window.location.href = '/login?redirect=' + encodeURIComponent(returnPath)
      return { success: false, error: 'Session expired. Please sign in again.', authError: true }
    }

    // Handle 423 Locked — the cutover read-only lock (see worker/readonly.ts).
    // Surfaced here rather than per-call so every write path reports it, and
    // announced on the window so the banner can react without prop-drilling.
    if (response.status === 423) {
      let message = 'Editing is disabled while DealHub moves to the PreSales Hub.'
      try {
        const body = await response.json()
        if (body?.error) message = body.error
      } catch { /* keep the fallback */ }
      window.dispatchEvent(new CustomEvent('dealhub:readonly', { detail: { message } }))
      return { success: false, error: message, readOnly: true }
    }

    // Check if response is JSON before parsing
    const contentType = response.headers.get('content-type') || ''
    if (!contentType.includes('application/json')) {
      return {
        success: false,
        error: `Server error (HTTP ${response.status}). Expected JSON but received ${contentType || 'unknown content type'}.`,
      }
    }

    const data = await response.json()

    if (!response.ok) {
      return {
        success: false,
        error: data.error || `HTTP ${response.status}`,
      }
    }

    return {
      success: true,
      data,
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
    }
  }
}

// =====================================================
// DEALS API
// =====================================================

// A deal in the review queue, enriched with the quote's denormalised metrics
// (for the homepage "In Review" tab). Quote/scope may be null on stub deals.
export interface ReviewQueueDeal extends Deal {
  quote: {
    id: number
    overall_assessment: string | null
    tcv: number | null
    arr: number | null
    gross_profit_margin: number | null
    implementation_fee: number | null
    implementation_fee_from_scope: number | null
  } | null
  scope: { id: number; total_cost: number | null } | null
}

// Result of the impl-team inject action (impl-review flow). The DB write always succeeds
// when this resolves successfully; slack_notification_status tells the FE whether the
// submitter ping went out so it can offer a "Retry notification" affordance (design §2.5).
export interface InjectImplFeeResult {
  before: number
  after: number
  source: 'scope_calc'
  completed_by: string | null
  completed_by_name: string | null
  slack_notification_status: 'ok' | 'failed'
}

export const dealsApi = {
  // List all deals
  async list(params?: {
    status?: string
    search?: string
    page?: number
    pageSize?: number
  }): Promise<ApiResponse<PaginatedResponse<Deal>>> {
    const queryParams: Record<string, string> = {}
    if (params?.status) queryParams.status = params.status
    if (params?.search) queryParams.search = params.search
    if (params?.page) queryParams.page = params.page.toString()
    if (params?.pageSize) queryParams.pageSize = params.pageSize.toString()

    return apiRequest<PaginatedResponse<Deal>>('/deals', { params: queryParams })
  },

  // Deals currently in a review step (Finance review or the Implementations
  // impl-fee loop) for the homepage "In Review" tab.
  async listReviewQueue(): Promise<ApiResponse<PaginatedResponse<ReviewQueueDeal>>> {
    return apiRequest<PaginatedResponse<ReviewQueueDeal>>('/deals', {
      params: { reviewQueue: '1', pageSize: '100' },
    })
  },

  // Deals that have reached final sign-off, for the homepage "Approved" tab.
  async listApproved(): Promise<ApiResponse<PaginatedResponse<ReviewQueueDeal>>> {
    return apiRequest<PaginatedResponse<ReviewQueueDeal>>('/deals', {
      params: { approved: '1', pageSize: '100' },
    })
  },

  // Search for reseller parent deals (resellerAgreement=yes, selling_to_customers)
  async searchResellerParents(search: string): Promise<ApiResponse<Array<{ id: number; customer_name: string }>>> {
    return apiRequest(`/deals/reseller-parents`, { params: { search } })
  },

  // Get single deal with all relations
  async get(id: number): Promise<ApiResponse<DealWithRelations>> {
    return apiRequest<DealWithRelations>(`/deals/${id}`)
  },

  // Create new deal
  async create(input: CreateDealInput): Promise<ApiResponse<Deal>> {
    return apiRequest<Deal>('/deals', {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },

  // Update deal
  async update(id: number, input: Partial<Deal>): Promise<ApiResponse<Deal>> {
    return apiRequest<Deal>(`/deals/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    })
  },

  // Delete deal
  async delete(id: number): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest<{ success: boolean }>(`/deals/${id}`, {
      method: 'DELETE',
    })
  },

  // Finalize deal
  async finalize(id: number, finalized_by: string): Promise<ApiResponse<Deal>> {
    return apiRequest<Deal>(`/deals/${id}/finalize`, {
      method: 'POST',
      body: JSON.stringify({ finalized_by }),
    })
  },

  // Link to HubSpot
  async linkHubSpot(
    id: number,
    hubspot_deal_id: string,
    hubspot_deal_name: string
  ): Promise<ApiResponse<Deal>> {
    return apiRequest<Deal>(`/deals/${id}/hubspot`, {
      method: 'POST',
      body: JSON.stringify({ hubspot_deal_id, hubspot_deal_name }),
    })
  },

  async clone(
    id: number,
    input?: {
      customer_name?: string
      created_by?: string
      created_by_name?: string
      hubspot_deal_id?: string
      hubspot_deal_name?: string
    }
  ): Promise<ApiResponse<Deal>> {
    return apiRequest<Deal>(`/deals/${id}/clone`, {
      method: 'POST',
      body: JSON.stringify(input || {}),
    })
  },

  // Get audit log / change history for a deal
  async getAuditLog(dealId: number, table?: string): Promise<ApiResponse<AuditLogEntry[]>> {
    const params: Record<string, string> = {}
    if (table) params.table = table
    return apiRequest<AuditLogEntry[]>(`/deals/${dealId}/audit-log`, { params })
  },

  // Impl team pushes the scope-calculated implementation fee into the quote (impl-review
  // flow, PR 4). Allowlist-gated server-side. quoteUrl/scopeUrl are surfaced as deep-links
  // in the Stage-2 Slack ping to the submitter.
  async injectImplFee(
    dealId: number,
    payload: { quoteUrl?: string; scopeUrl?: string } = {}
  ): Promise<ApiResponse<InjectImplFeeResult>> {
    return apiRequest<InjectImplFeeResult>(`/deals/${dealId}/quote/inject-impl-fee`, {
      method: 'POST',
      body: JSON.stringify({ source: 'scope_calc', ...payload }),
    })
  },
}

// =====================================================
// TRANSCRIPTS API
// =====================================================
export interface DealTranscript {
  id: number
  deal_id: number
  title: string
  call_date: string
  transcript: string
  created_by: string | null
  created_at: string
  updated_at: string
}

export const transcriptsApi = {
  async list(dealId: number): Promise<ApiResponse<DealTranscript[]>> {
    return apiRequest<DealTranscript[]>(`/deals/${dealId}/transcripts`)
  },

  async create(dealId: number, data: { title: string; callDate: string; transcript: string }): Promise<ApiResponse<DealTranscript>> {
    return apiRequest<DealTranscript>(`/deals/${dealId}/transcripts`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async update(dealId: number, transcriptId: number, data: { title?: string; callDate?: string; transcript?: string }): Promise<ApiResponse<DealTranscript>> {
    return apiRequest<DealTranscript>(`/deals/${dealId}/transcripts/${transcriptId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  async remove(dealId: number, transcriptId: number): Promise<ApiResponse<{ deleted: boolean }>> {
    return apiRequest<{ deleted: boolean }>(`/deals/${dealId}/transcripts/${transcriptId}`, {
      method: 'DELETE',
    })
  },

  async generateBrainSummary(dealId: number, transcriptIds: number[]): Promise<ApiResponse<{ summary: string; transcript_count: number }>> {
    return apiRequest<{ summary: string; transcript_count: number }>(`/deals/${dealId}/brain-summary`, {
      method: 'POST',
      body: JSON.stringify({ transcript_ids: transcriptIds }),
    })
  },
}

// =====================================================
// HUBSPOT EXCERPTS API
// =====================================================
export interface HubspotExcerptSource {
  type: string
  count: number
}

export interface HubspotExcerpt {
  id: number
  deal_id: number
  excerpt: string
  activity_count: number
  sources_json: string | null
  generated_at: string
  created_by: string | null
  created_at: string
  updated_at: string
}

export const hubspotExcerptsApi = {
  async get(dealId: number): Promise<ApiResponse<HubspotExcerpt | null>> {
    return apiRequest<HubspotExcerpt | null>(`/deals/${dealId}/hubspot-excerpt`)
  },

  async generate(dealId: number): Promise<ApiResponse<HubspotExcerpt>> {
    return apiRequest<HubspotExcerpt>(`/deals/${dealId}/hubspot-excerpt`, {
      method: 'POST',
      body: JSON.stringify({}),
    })
  },
}

// =====================================================
// QUOTES API
// =====================================================
export const quotesApi = {
  // Get quote by deal ID
  async getByDealId(dealId: number): Promise<ApiResponse<Quote>> {
    return apiRequest<Quote>(`/deals/${dealId}/quote`)
  },

  // Create or update quote for a deal
  async save(dealId: number, quote: Partial<Quote>): Promise<ApiResponse<Quote>> {
    return apiRequest<Quote>(`/deals/${dealId}/quote`, {
      method: 'PUT',
      body: JSON.stringify(quote),
    })
  },

  // Sync implementation fee from scope
  async syncFromScope(dealId: number): Promise<ApiResponse<Quote>> {
    return apiRequest<Quote>(`/deals/${dealId}/quote/sync-scope`, {
      method: 'POST',
    })
  },

  // Reassign quote to a different deal
  async reassign(currentDealId: number, targetDealId: number): Promise<ApiResponse<{ success: boolean; new_deal_id: number }>> {
    return apiRequest(`/deals/${currentDealId}/quote/reassign`, {
      method: 'POST',
      body: JSON.stringify({ target_deal_id: targetDealId }),
    })
  },

  // List scopes from related deals (same customer) that can be linked to this quote
  async availableScopes(dealId: number, search?: string): Promise<ApiResponse<{
    scopes: Array<{
      id: number
      deal_id: number
      total_hours: number
      total_cost: number
      scoped_by_name: string | null
      updated_at: string
      created_at: string
      customer_name: string
      hubspot_deal_id: string | null
      hubspot_deal_name: string | null
      configured_sections: number
      related: number
    }>
  }>> {
    const params = search ? `?search=${encodeURIComponent(search)}` : ''
    return apiRequest(`/deals/${dealId}/quote/available-scopes${params}`)
  },

  // List the immutable approved snapshots for a deal's quote (newest first).
  async listVersions(dealId: number): Promise<ApiResponse<{ versions: QuoteVersionSummary[] }>> {
    return apiRequest(`/deals/${dealId}/quote/versions`)
  },

  // Get one approved snapshot, including the full quote payload for read-only render.
  async getVersion(dealId: number, versionId: number): Promise<ApiResponse<QuoteVersionDetail>> {
    return apiRequest(`/deals/${dealId}/quote/versions/${versionId}`)
  },
}

// An approved quote snapshot (captured at finalise). Summary drives the Versions list;
// `quote` (on the detail) is the full payload rendered read-only.
export interface QuoteVersionSummary {
  id: number
  version_number: number
  approved_by: string | null
  approved_by_name: string | null
  approved_at: string
  customer_plan: string | null
  currency: string | null
  implementation_fee: number | null
  tcv: number | null
  arr: number | null
  gross_profit_margin: number | null
  overall_assessment: string | null
}

export interface QuoteVersionDetail {
  id: number
  version_number: number
  approved_by: string | null
  approved_by_name: string | null
  approved_at: string
  quote: Quote | null
}

// =====================================================
// SCOPES API
// =====================================================
export const scopesApi = {
  // Get scope by deal ID
  async getByDealId(dealId: number): Promise<ApiResponse<Scope>> {
    return apiRequest<Scope>(`/deals/${dealId}/scope`)
  },

  // Create or update scope for a deal
  async save(dealId: number, scope: Partial<Scope>): Promise<ApiResponse<Scope>> {
    return apiRequest<Scope>(`/deals/${dealId}/scope`, {
      method: 'PUT',
      body: JSON.stringify(scope),
    })
  },

  // Get scope hours
  async getHours(scopeId: number): Promise<ApiResponse<{ hours: ScopeHour[] }>> {
    return apiRequest<{ hours: ScopeHour[] }>(`/scopes/${scopeId}/hours`)
  },

  // Recalculate hours based on configuration
  async recalculateHours(scopeId: number): Promise<ApiResponse<Scope>> {
    return apiRequest<Scope>(`/scopes/${scopeId}/recalculate`, {
      method: 'POST',
    })
  },

  // Get scope field-level change log
  async getLog(dealId: number, limit = 200, offset = 0): Promise<ApiResponse<{
    entries: Array<{
      id: number
      user_email: string | null
      user_name: string | null
      section: string
      field_path: string
      old_value: string
      new_value: string
      created_at: string
    }>
    total: number
    limit: number
    offset: number
  }>> {
    return apiRequest(`/deals/${dealId}/scope-log?limit=${limit}&offset=${offset}`)
  },
}

export const scopeVersionsApi = {
  async list(dealId: number): Promise<ApiResponse<{
    versions: Array<{
      id: number
      version_number: number
      version_label: string | null
      version_type: string
      proposal_status: string | null
      ai_source: string | null
      total_hours: number
      total_cost: number
      created_by: string | null
      created_by_name: string | null
      created_at: string
      reviewed_by: string | null
      reviewed_at: string | null
    }>
    total: number
  }>> {
    return apiRequest(`/deals/${dealId}/scope/versions`)
  },

  async get(dealId: number, versionId: number): Promise<ApiResponse<Record<string, unknown>>> {
    return apiRequest(`/deals/${dealId}/scope/versions/${versionId}`)
  },

  async create(dealId: number, label?: string): Promise<ApiResponse<{ id: number }>> {
    return apiRequest(`/deals/${dealId}/scope/versions`, {
      method: 'POST',
      body: JSON.stringify({ label }),
    })
  },

  async restore(dealId: number, versionId: number): Promise<ApiResponse<{ restored_version: number }>> {
    return apiRequest(`/deals/${dealId}/scope/versions/${versionId}/restore`, {
      method: 'POST',
    })
  },

  async blueprintSuggest(dealId: number, userNotes: string, includeHubspot: boolean): Promise<ApiResponse<{
    suggestions: {
      problemHypothesis?: string | null
      surfacePain?: string | null
      deepPain?: string | null
      stakeholders?: { name: string; role: string; technicalConcerns: string; engagementState: 'evaluator' | 'champion_candidate' | 'champion' | 'blocker' | 'unknown' }[] | null
      countryRulesets?: { country: string; ruleset: string; notes: string; docScanVendors?: string[]; selfieVendors?: string[]; deviceDataVendors?: string[]; nationalIds?: string[]; passRate?: string; desiredPassRate?: string }[] | null
    }
    confidence: 'high' | 'medium' | 'low'
    reasoning: string
    warnings: string[]
  }>> {
    return apiRequest(`/deals/${dealId}/scope/blueprint-suggest`, { method: 'POST', body: JSON.stringify({ userNotes, includeHubspot }) })
  },

  async blueprintNextSteps(dealId: number, qualification?: Record<string, string> | null): Promise<ApiResponse<{
    items: { priority: number; text: string }[]
    error?: string
  }>> {
    return apiRequest(`/deals/${dealId}/scope/blueprint-next-steps`, { method: 'POST', body: JSON.stringify({ qualification: qualification || undefined }) })
  },

  async scopeSuggest(dealId: number, userNotes: string, includeHubspot: boolean): Promise<ApiResponse<{
    suggestions: {
      general?: {
        clientUseCase?: string | null
        phases?: string[] | null
        integrationType?: string | null
        platformVersion?: string | null
        forecastChecks12m?: number | null
        ssoAccess?: string | null
        aiFeatures?: string | null
      } | null
      kyc?: {
        kycRequired?: string | null
        complianceFramework?: string | null
        internationalCountries?: string[] | null
      } | null
      aml?: {
        amlRequired?: string | null
        level?: string | null
        monitoringType?: string | null
      } | null
      biometrics?: {
        documentVerification?: string | null
        selfieVerification?: string | null
      } | null
      kyb?: {
        enabled?: string | null
      } | null
    }
    confidence: 'high' | 'medium' | 'low'
    reasoning: string
    warnings: string[]
  }>> {
    return apiRequest(`/deals/${dealId}/scope/scope-suggest`, { method: 'POST', body: JSON.stringify({ userNotes, includeHubspot }) })
  },

  async aiPropose(dealId: number): Promise<ApiResponse<{
    version_id: number
    version_number: number
    changes: Array<{ field: string; value: unknown; reason: string }>
    summary: string
    confidence_notes: string
  }>> {
    return apiRequest(`/deals/${dealId}/scope/ai-propose`, { method: 'POST' })
  },

  async acceptProposal(dealId: number, versionId: number, cherryPick?: Record<string, unknown>): Promise<ApiResponse<{ status: string }>> {
    return apiRequest(`/deals/${dealId}/scope/versions/${versionId}/accept`, {
      method: 'POST',
      body: JSON.stringify({ cherry_pick: cherryPick }),
    })
  },

  async rejectProposal(dealId: number, versionId: number): Promise<ApiResponse<{ status: string }>> {
    return apiRequest(`/deals/${dealId}/scope/versions/${versionId}/reject`, {
      method: 'POST',
    })
  },

  async getParentScope(dealId: number): Promise<ApiResponse<{ parent: Record<string, unknown> | null }>> {
    return apiRequest(`/deals/${dealId}/scope/parent`)
  },

  async setParentScope(dealId: number, parentScopeId: number | null): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest(`/deals/${dealId}/scope/parent`, {
      method: 'PUT',
      body: JSON.stringify({ parent_scope_id: parentScopeId }),
    })
  },

  async getChildScopes(dealId: number): Promise<ApiResponse<{ children: ResellerChildScope[] }>> {
    return apiRequest(`/deals/${dealId}/scope/children`)
  },
}

export interface ResellerChildScope {
  scope_id: number
  deal_id: number
  customer_name: string | null
  hubspot_deal_id: string | null
  hubspot_deal_name: string | null
}

// =====================================================
// ROI API
// =====================================================
export const roiApi = {
  // List all ROI calculations
  async list(params?: {
    deal_id?: number
    search?: string
  }): Promise<ApiResponse<RoiCalculation[]>> {
    const queryParams: Record<string, string> = {}
    if (params?.deal_id) queryParams.deal_id = params.deal_id.toString()
    if (params?.search) queryParams.search = params.search

    return apiRequest<RoiCalculation[]>('/roi', { params: queryParams })
  },

  // Get ROI by ID
  async get(id: number): Promise<ApiResponse<RoiCalculation>> {
    return apiRequest<RoiCalculation>(`/roi/${id}`)
  },

  // Get ROI by deal ID
  async getByDealId(dealId: number): Promise<ApiResponse<RoiCalculation>> {
    return apiRequest<RoiCalculation>(`/deals/${dealId}/roi`)
  },

  // Create ROI calculation
  async create(roi: Partial<RoiCalculation>): Promise<ApiResponse<RoiCalculation>> {
    return apiRequest<RoiCalculation>('/roi', {
      method: 'POST',
      body: JSON.stringify(roi),
    })
  },

  // Update ROI calculation
  async update(id: number, roi: Partial<RoiCalculation>): Promise<ApiResponse<RoiCalculation>> {
    return apiRequest<RoiCalculation>(`/roi/${id}`, {
      method: 'PUT',
      body: JSON.stringify(roi),
    })
  },

  // Link ROI to deal
  async linkToDeal(roiId: number, dealId: number): Promise<ApiResponse<RoiCalculation>> {
    return apiRequest<RoiCalculation>(`/roi/${roiId}/link`, {
      method: 'POST',
      body: JSON.stringify({ deal_id: dealId }),
    })
  },

  // Calculate ROI outputs from inputs
  async calculate(inputs: RoiInputs): Promise<ApiResponse<RoiOutputs>> {
    return apiRequest<RoiOutputs>('/roi/calculate', {
      method: 'POST',
      body: JSON.stringify(inputs),
    })
  },
}

// =====================================================
// DELIVERABLES API
// =====================================================
export const deliverablesApi = {
  // Get deliverables for a deal
  async getByDealId(dealId: number): Promise<ApiResponse<Deliverable[]>> {
    return apiRequest<Deliverable[]>(`/deals/${dealId}/deliverables`)
  },

  // Update deliverable status
  async updateStatus(
    id: number,
    status: DeliverableStatus
  ): Promise<ApiResponse<Deliverable>> {
    return apiRequest<Deliverable>(`/deliverables/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    })
  },

  // Generate deliverables from scope
  async generateFromScope(dealId: number): Promise<ApiResponse<Deliverable[]>> {
    return apiRequest<Deliverable[]>(`/deals/${dealId}/deliverables/generate`, {
      method: 'POST',
    })
  },
}

// =====================================================
// PRODUCTS API
// =====================================================
export interface ProductChange {
  id: number
  user_email: string
  change_type: string
  details: string
  product_ids: string
  created_at: string
}

export interface BulkUploadResult {
  success: boolean
  inserted: number
  updated: number
  skipped: number
}

export const productsApi = {
  // Get all products
  async list(): Promise<ApiResponse<Product[]>> {
    return apiRequest<Product[]>('/products')
  },

  // Search products
  async search(query: string): Promise<ApiResponse<Product[]>> {
    return apiRequest<Product[]>('/products/search', {
      params: { q: query },
    })
  },

  // Batch save edited products (admin)
  async bulkSave(products: Array<Record<string, unknown>>): Promise<ApiResponse<{ success: boolean; updated: number }>> {
    return apiRequest('/products/bulk', {
      method: 'PUT',
      // Auth headers added automatically by apiRequest()
      body: JSON.stringify({ products }),
    })
  },

  // Bulk upload products (admin)
  async bulkUpload(mode: 'replace' | 'update' | 'append', products: Array<Record<string, unknown>>): Promise<ApiResponse<BulkUploadResult>> {
    return apiRequest('/products/bulk-upload', {
      method: 'POST',
      // Auth headers added automatically by apiRequest()
      body: JSON.stringify({ mode, products }),
    })
  },

  // Get change history (admin)
  async getHistory(limit = 100, offset = 0): Promise<ApiResponse<ProductChange[]>> {
    return apiRequest<ProductChange[]>('/products/history', {
      // Auth headers added automatically by apiRequest()
      params: { limit: String(limit), offset: String(offset) },
    })
  },

  // Download the products CSV.
  // We can't just `window.open(url)` — the browser navigation doesn't send
  // the Authorization header, so the worker returns 401. Instead fetch with
  // auth, materialise a blob, and trigger a client-side download.
  async exportCsv(): Promise<ApiResponse<true>> {
    try {
      const base = import.meta.env.VITE_API_URL || '/api'
      const response = await fetch(`${base}/products/export`, {
        headers: getAuthHeaders(),
      })

      if (!response.ok) {
        if (response.status === 401) {
          return { success: false, error: 'Session expired. Please sign in again.', authError: true }
        }
        return { success: false, error: `Export failed (HTTP ${response.status}).` }
      }

      const blob = await response.blob()

      // Pull the suggested filename out of Content-Disposition if the worker
      // sent one (it does: `attachment; filename="products_YYYY-MM-DD.csv"`).
      let filename = `products_${new Date().toISOString().slice(0, 10)}.csv`
      const disposition = response.headers.get('Content-Disposition') || ''
      const match = disposition.match(/filename\s*=\s*"?([^";]+)"?/i)
      if (match?.[1]) filename = match[1]

      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      return { success: true, data: true }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Network error'
      return { success: false, error: `Export failed: ${message}` }
    }
  },
}

// =====================================================
// TARGET ACCOUNTS API
// =====================================================
export interface TargetAccount {
  id: string
  name: string
  domain: string | null
  sector: string | null
  country: string | null
  city: string | null
  hubspot_owner_id: string | null
  employees: number | null
  revenue: number | null
  icp_tier: string | null
  volume_score: 'high' | 'medium' | 'low' | null
  international_score: 'high' | 'medium' | 'low' | null
  multiproduct_score: 'high' | 'medium' | 'low' | null
  icp_fit_score: 'excellent' | 'good' | 'medium' | 'low' | null
  overall_icp_score: 'excellent' | 'good' | 'medium' | 'low' | null
  // Additional fields
  industry: string | null
  lifecyclestage: string | null
  type: string | null
  description: string | null
  phone: string | null
  website: string | null
  linkedin_company_page: string | null
  num_associated_contacts: number | null
  num_associated_deals: number | null
  notes_last_contacted: string | null
  createdate: string | null
  hs_lastmodifieddate: string | null
  account_status: 'active' | 'engaged' | 'archived' | null
}

export interface Owner {
  id: string
  email: string
  first_name: string | null
  last_name: string | null
}

export interface TargetAccountsResponse {
  accounts: TargetAccount[]
  owners: Owner[]
  total: number
}

export const targetAccountsApi = {
  // Get all target accounts with ICP scores
  async list(): Promise<ApiResponse<TargetAccountsResponse>> {
    return apiRequest<TargetAccountsResponse>('/target-accounts')
  },

  // Get single target account by ID
  async get(id: string): Promise<ApiResponse<TargetAccount>> {
    return apiRequest<TargetAccount>(`/target-accounts/${id}`)
  },
}

// =====================================================
// HUBSPOT API
// =====================================================
export interface HubSpotDeal {
  id: string
  properties: {
    dealname: string
    amount: string
    dealstage: string
    total_presales_hours?: string
  }
}

export const hubspotApi = {
  // Search HubSpot deals
  async searchDeals(query: string): Promise<ApiResponse<{ results: HubSpotDeal[] }>> {
    return apiRequest('/hubspot/search', {
      params: { q: query },
    })
  },

  // Get HubSpot deal by ID
  async getDeal(dealId: string): Promise<ApiResponse<HubSpotDeal>> {
    return apiRequest(`/hubspot/deals/${dealId}`)
  },

  // Extract company name & ABN from latest contract attached to a HubSpot deal
  async extractContractDetails(dealId: string): Promise<ApiResponse<{ companyName: string; abn: string; fileName: string }>> {
    return apiRequest(`/hubspot/deals/${dealId}/contract-details`)
  },

  // Get qualification properties for a HubSpot deal
  async getQualification(hubspotDealId: string): Promise<ApiResponse<{ qualification: Record<string, string> }>> {
    return apiRequest(`/hubspot/deals/${hubspotDealId}/qualification`)
  },

  // Get attachments for a HubSpot deal (latest MSA first)
  async getAttachments(hubspotDealId: string): Promise<ApiResponse<{ attachments: Array<{ id: string; name: string; timestamp: string; isMsa: boolean; signedUrl?: string }> }>> {
    return apiRequest(`/hubspot/deals/${hubspotDealId}/attachments`)
  },

  // Log activity to HubSpot
  async logActivity(
    dealId: string,
    type: 'note' | 'call' | 'email',
    content: string
  ): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest(`/hubspot/deals/${dealId}/activity`, {
      method: 'POST',
      body: JSON.stringify({ type, content }),
    })
  },
}

// =====================================================
// USER PREFERENCES API
// =====================================================
export interface UserPreferences {
  user_id: string
  user_email: string | null
  target_accounts_columns: string[] | null
  target_accounts_sort_field: string
  target_accounts_sort_order: string
  onboarding_acknowledged: string[]
}

export const preferencesApi = {
  // Get user preferences
  async get(userId: string): Promise<ApiResponse<{ preferences: UserPreferences | null }>> {
    return apiRequest(`/preferences/${encodeURIComponent(userId)}`)
  },

  // Save user preferences
  async save(prefs: {
    user_id: string
    user_email?: string
    target_accounts_columns?: string[]
    target_accounts_sort_field?: string
    target_accounts_sort_order?: string
    onboarding_acknowledged?: string[]
  }): Promise<ApiResponse<{ preferences: UserPreferences }>> {
    return apiRequest('/preferences', {
      method: 'POST',
      body: JSON.stringify(prefs),
    })
  },
}

// =====================================================
// SCHEMA OVERRIDES API
// =====================================================
export type SchemaOverridesMap = Record<string, Record<string, string>>

export const schemaOverridesApi = {
  // Fetch the full map of global schema overrides.
  async getAll(): Promise<ApiResponse<{ overridesByKey: SchemaOverridesMap }>> {
    return apiRequest('/schema-overrides')
  },

  // Upsert overrides for a single entity. Server replaces all props for the
  // given entityKey with the provided object (empty values cause delete).
  async save(
    entityKey: string,
    overrides: Record<string, string>,
  ): Promise<ApiResponse<{ entityKey: string; overrides: Record<string, string> | null }>> {
    return apiRequest('/schema-overrides', {
      method: 'PUT',
      body: JSON.stringify({ entityKey, overrides }),
    })
  },

  async remove(entityKey: string): Promise<ApiResponse<{ entityKey: string }>> {
    return apiRequest(`/schema-overrides/${encodeURIComponent(entityKey)}`, {
      method: 'DELETE',
    })
  },
}

// =====================================================
// ACCOUNT AI PROMPTS API
// =====================================================
export interface AccountPrompt {
  id: number
  account_id: string
  account_name: string | null
  user_email: string
  user_name: string | null
  prompt: string
  response: string
  model: string | null
  tokens_used: number | null
  hubspot_note_id: string | null
  created_at: string
}

export interface AskPromptRequest {
  prompt: string
  user_email: string
  user_name?: string
  account_name?: string
  log_to_hubspot?: boolean
}

export interface AskPromptResponse extends AccountPrompt {}

export interface PromptHistoryResponse {
  prompts: AccountPrompt[]
  total: number
  limit: number
  offset: number
}

export const accountPromptsApi = {
  // Ask a question about an account
  async ask(accountId: string, request: AskPromptRequest): Promise<ApiResponse<AskPromptResponse>> {
    return apiRequest(`/accounts/${accountId}/prompts`, {
      method: 'POST',
      body: JSON.stringify(request),
    })
  },

  // Get prompt history for an account
  async getHistory(accountId: string, params?: {
    limit?: number
    offset?: number
  }): Promise<ApiResponse<PromptHistoryResponse>> {
    const queryParams: Record<string, string> = {}
    if (params?.limit) queryParams.limit = params.limit.toString()
    if (params?.offset) queryParams.offset = params.offset.toString()

    return apiRequest(`/accounts/${accountId}/prompts`, { params: queryParams })
  },

  // Get recent prompts across all accounts
  async getRecent(limit = 10): Promise<ApiResponse<AccountPrompt[]>> {
    return apiRequest('/prompts/recent', {
      params: { limit: limit.toString() },
    })
  },
}

// =====================================================
// FEEDBACK API
// =====================================================
import type { FeedbackTicket, SubmitFeedbackInput, SubmitFeedbackResult, RefineInput, RefineResult } from '@features/feedback/types'

export const feedbackApi = {
  // Refine feedback with AI (probing questions + structured rewrite)
  async refine(input: RefineInput): Promise<ApiResponse<RefineResult>> {
    return apiRequest('/feedback/refine', {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },

  // List all tickets
  async list(params?: { status?: string; type?: string }): Promise<ApiResponse<{ tickets: FeedbackTicket[] }>> {
    const queryParams: Record<string, string> = {}
    if (params?.status) queryParams.status = params.status
    if (params?.type) queryParams.type = params.type
    return apiRequest('/feedback', { params: queryParams })
  },

  // Get single ticket
  async get(id: number): Promise<ApiResponse<FeedbackTicket>> {
    return apiRequest(`/feedback/${id}`)
  },

  // Submit feedback (AI classifies)
  async submit(input: SubmitFeedbackInput): Promise<ApiResponse<SubmitFeedbackResult>> {
    return apiRequest('/feedback', {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },

  // Update ticket status (kanban drag)
  async updateStatus(id: number, status: string): Promise<ApiResponse<FeedbackTicket>> {
    return apiRequest(`/feedback/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    })
  },

  // Update ticket (general)
  async update(id: number, input: Partial<FeedbackTicket>): Promise<ApiResponse<FeedbackTicket>> {
    return apiRequest(`/feedback/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    })
  },

  // Delete ticket
  async delete(id: number): Promise<ApiResponse<{ deleted: boolean; id: number }>> {
    return apiRequest(`/feedback/${id}`, {
      method: 'DELETE',
    })
  },
}

// =====================================================
// RECIPES API (public-facing use-case templates)
// =====================================================
export const recipesApi = {
  // List all recipes (public, filterable)
  async list(params?: {
    status?: string
    region?: string
    industry?: string
    product?: string
    public?: boolean
  }): Promise<ApiResponse<Recipe[]>> {
    const queryParams: Record<string, string> = {}
    if (params?.status) queryParams.status = params.status
    if (params?.region) queryParams.region = params.region
    if (params?.industry) queryParams.industry = params.industry
    if (params?.product) queryParams.product = params.product
    if (params?.public !== undefined) queryParams.public = params.public.toString()

    return apiRequest<Recipe[]>('/recipes', { params: queryParams })
  },

  // Get single recipe by ID
  async get(id: number): Promise<ApiResponse<Recipe>> {
    return apiRequest<Recipe>(`/recipes/${id}`)
  },

  // Create recipe (admin)
  async create(recipe: Partial<Recipe>): Promise<ApiResponse<Recipe>> {
    return apiRequest<Recipe>('/recipes', {
      method: 'POST',
      body: JSON.stringify(recipe),
    })
  },

  // Update recipe (admin)
  async update(id: number, recipe: Partial<Recipe>): Promise<ApiResponse<Recipe>> {
    return apiRequest<Recipe>(`/recipes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(recipe),
    })
  },

  // Delete recipe (admin)
  async delete(id: number): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest<{ success: boolean }>(`/recipes/${id}`, {
      method: 'DELETE',
    })
  },
}

// =====================================================
// DRAFTS API (save work without a deal session)
// =====================================================
export const draftsApi = {
  // List drafts for a user
  async list(params?: { user_email?: string; type?: string }): Promise<ApiResponse<Draft[]>> {
    const queryParams: Record<string, string> = {}
    if (params?.user_email) queryParams.user_email = params.user_email
    if (params?.type) queryParams.type = params.type
    return apiRequest<Draft[]>('/drafts', { params: queryParams })
  },

  // Get a single draft
  async get(id: number): Promise<ApiResponse<Draft>> {
    return apiRequest<Draft>(`/drafts/${id}`)
  },

  // Create a new draft
  async create(draft: Partial<Draft>): Promise<ApiResponse<Draft>> {
    return apiRequest<Draft>('/drafts', {
      method: 'POST',
      body: JSON.stringify(draft),
    })
  },

  // Update a draft (auto-save target)
  async update(id: number, draft: Partial<Draft>): Promise<ApiResponse<Draft>> {
    return apiRequest<Draft>(`/drafts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(draft),
    })
  },

  // Delete a draft
  async delete(id: number): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest<{ success: boolean }>(`/drafts/${id}`, {
      method: 'DELETE',
    })
  },

  // Convert draft to a deal session
  async convert(id: number, data?: { hubspot_deal_id?: string; hubspot_deal_name?: string }): Promise<ApiResponse<{ dealId: number }>> {
    return apiRequest<{ dealId: number }>(`/drafts/${id}/convert`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    })
  },
}

// =====================================================
// PRESALES DASHBOARD API
// =====================================================
export const presalesDashboardApi = {
  // Get presales deals
  async getDeals(params?: {
    owner?: string
    status?: string
    search?: string
    page?: number
    pageSize?: number
  }): Promise<ApiResponse<PaginatedResponse<PresalesDeal>>> {
    const queryParams: Record<string, string> = {}
    if (params?.owner) queryParams.owner = params.owner
    if (params?.status) queryParams.status = params.status
    if (params?.search) queryParams.search = params.search
    if (params?.page) queryParams.page = params.page.toString()
    if (params?.pageSize) queryParams.pageSize = params.pageSize.toString()

    return apiRequest<PaginatedResponse<PresalesDeal>>('/presales-dashboard/deals', {
      params: queryParams,
    })
  },

  // Sync from HubSpot
  async syncFromHubSpot(): Promise<ApiResponse<PresalesSyncResult>> {
    return apiRequest<PresalesSyncResult>('/presales-dashboard/sync', { method: 'POST' })
  },

  // Get KR1 metrics (Win Ratio)
  async getKR1(params?: { period?: string; owner?: string }): Promise<ApiResponse<PresalesKR1Result>> {
    const queryParams: Record<string, string> = {}
    if (params?.period) queryParams.period = params.period
    if (params?.owner) queryParams.owner = params.owner

    return apiRequest<PresalesKR1Result>('/presales-dashboard/kr/1', { params: queryParams })
  },

  // Get KR1 rolling metrics (Win Ratio with TTM, Rolling 6M, cohorts)
  async getKR1Rolling(params?: { period?: string }): Promise<ApiResponse<KR1RollingResult>> {
    const queryParams: Record<string, string> = {}
    if (params?.period) queryParams.period = params.period
    return apiRequest<KR1RollingResult>('/presales-dashboard/kr/1/rolling', { params: queryParams })
  },

  // Get KR2 metrics (Time to Close)
  async getKR2(params?: { period?: string; owner?: string }): Promise<ApiResponse<PresalesKR2Result>> {
    const queryParams: Record<string, string> = {}
    if (params?.period) queryParams.period = params.period
    if (params?.owner) queryParams.owner = params.owner

    return apiRequest<PresalesKR2Result>('/presales-dashboard/kr/2', { params: queryParams })
  },

  // Get KR2 rolling metrics (Time to Close with TTM, Rolling 6M, cohorts)
  async getKR2Rolling(params?: { period?: string }): Promise<ApiResponse<KR2RollingResult>> {
    const queryParams: Record<string, string> = {}
    if (params?.period) queryParams.period = params.period
    return apiRequest<KR2RollingResult>('/presales-dashboard/kr/2/rolling', { params: queryParams })
  },

  // Get KR1 deal-level detail for verification
  async getKR1Deals(params?: { period?: string }): Promise<ApiResponse<{ deals: KR1Deal[] }>> {
    const queryParams: Record<string, string> = {}
    if (params?.period) queryParams.period = params.period
    return apiRequest<{ deals: KR1Deal[] }>('/presales-dashboard/kr/1/deals', { params: queryParams })
  },

  // Get KR2 deal-level detail for verification
  async getKR2Deals(params?: { period?: string }): Promise<ApiResponse<KR2DealsResponse>> {
    const queryParams: Record<string, string> = {}
    if (params?.period) queryParams.period = params.period
    return apiRequest<KR2DealsResponse>('/presales-dashboard/kr/2/deals', { params: queryParams })
  },

  // Get KR3 metrics (Demos per person)
  async getKR3(params?: { quarter?: string; owner?: string }): Promise<ApiResponse<PresalesKR3Result>> {
    const queryParams: Record<string, string> = {}
    if (params?.quarter) queryParams.quarter = params.quarter
    if (params?.owner) queryParams.owner = params.owner

    return apiRequest<PresalesKR3Result>('/presales-dashboard/kr/3', { params: queryParams })
  },

  // Get KR3 demo-level detail for verification
  async getKR3Demos(params?: { period?: string }): Promise<ApiResponse<{ demos: KR3Demo[] }>> {
    const queryParams: Record<string, string> = {}
    if (params?.period) queryParams.period = params.period
    return apiRequest<{ demos: KR3Demo[] }>('/presales-dashboard/kr/3/demos', { params: queryParams })
  },

  // Get KR4 metrics (Playbook completion)
  async getKR4(params?: { quarter?: string; owner?: string }): Promise<ApiResponse<PresalesKR4Result>> {
    const queryParams: Record<string, string> = {}
    if (params?.quarter) queryParams.quarter = params.quarter
    if (params?.owner) queryParams.owner = params.owner

    return apiRequest<PresalesKR4Result>('/presales-dashboard/kr/4', { params: queryParams })
  },

  // Update a KR4 deliverable percentage
  async updateDeliverablePercentage(id: string, percentage: number) {
    return apiRequest('/presales-dashboard/kr/4/deliverables', {
      method: 'PUT',
      body: JSON.stringify({ id, percentage }),
    })
  },

  // Get summary stats
  async getSummary(): Promise<
    ApiResponse<{
      total_deals: number
      active_deals: number
      by_stage: Array<{ deal_stage: string; count: number }>
      by_owner: Array<{ owner: string; count: number }>
    }>
  > {
    return apiRequest('/presales-dashboard/summary')
  },

  // ===== Calendar Integration =====

  // Test calendar connection
  async testCalendarConnection(): Promise<
    ApiResponse<{
      configured: boolean
      all_calendars_accessible: boolean
      tested_calendars: number
      results: Array<{
        calendar_id: string
        success: boolean
        events_found?: number
        error?: string
      }>
    }>
  > {
    return apiRequest('/presales-dashboard/calendar-test')
  },

  // Get calendar events (fetched live from Google Calendar API)
  async getCalendarEvents(params?: {
    startDate?: string
    endDate?: string
    unlinkedOnly?: boolean
  }): Promise<ApiResponse<CalendarEventsResponse>> {
    const queryParams: Record<string, string> = {}
    if (params?.startDate) queryParams.startDate = params.startDate
    if (params?.endDate) queryParams.endDate = params.endDate
    if (params?.unlinkedOnly) queryParams.unlinkedOnly = 'true'

    return apiRequest('/presales-dashboard/calendar-events', { params: queryParams })
  },

  // Lookup event by Google Calendar event ID (used by Chrome extension)
  async lookupEventByGoogleId(googleEventId: string): Promise<ApiResponse<{ event: any; link: any }>> {
    return apiRequest('/presales-dashboard/calendar-events/lookup', {
      params: { googleEventId },
    })
  },

  // Link event to deal (or prospect when deal_id is null)
  async linkEvent(data: {
    event_id?: number
    google_event_id?: string
    google_calendar_id?: string
    deal_id: number | null
    category: string
    notes?: string
    linked_by?: string
    prospect_company?: string
    presales_attendees?: string[]
    presales_minutes?: number
    frankieone_attendee_count?: number
    frankieone_total_minutes?: number
  }): Promise<ApiResponse<EventLinkResult>> {
    return apiRequest('/presales-dashboard/event-links', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // Unlink event from deal
  async unlinkEvent(linkId: number): Promise<ApiResponse<UnlinkResult>> {
    return apiRequest(`/presales-dashboard/event-links/${linkId}`, {
      method: 'DELETE',
    })
  },

  // Reassign a single event link to a real deal
  async reassignEventLink(linkId: number, dealId: number): Promise<ApiResponse<{ success: boolean; hours_updated: number }>> {
    return apiRequest(`/presales-dashboard/event-links/${linkId}/reassign`, {
      method: 'PUT',
      body: JSON.stringify({ deal_id: dealId }),
    })
  },

  // Reassign ALL prospect links for a company to a real deal
  async reassignBulkProspectEvents(prospectCompany: string, dealId: number): Promise<ApiResponse<{ success: boolean; reassigned: number; hours_updated: number }>> {
    return apiRequest('/presales-dashboard/event-links/reassign-bulk', {
      method: 'PUT',
      body: JSON.stringify({ prospect_company: prospectCompany, deal_id: dealId }),
    })
  },

  // Get prospect company summaries (unattributed events)
  async getProspectSummaries(): Promise<ApiResponse<ProspectSummary[]>> {
    return apiRequest('/presales-dashboard/event-links/prospects')
  },

  // Review suggestion (accept or reject)
  async reviewSuggestion(suggestionId: number, data: {
    status: 'accepted' | 'rejected'
    category?: string
    reviewed_by?: string
  }): Promise<ApiResponse<ReviewSuggestionResult>> {
    return apiRequest(`/presales-dashboard/event-suggestions/${suggestionId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  // Get event link time tracking summary
  async getEventLinkSummary(): Promise<ApiResponse<EventLinkSummary>> {
    return apiRequest('/presales-dashboard/event-links/summary')
  },

  // ===== Slack Integration =====

  // Test Slack bot connection
  async testSlackConnection(): Promise<ApiResponse<{
    configured: boolean
    bot_info?: { bot_id: string; app_id: string; name: string }
    error?: string
  }>> {
    return apiRequest('/presales-dashboard/slack/test-connection')
  },

  // Get Slack activity for a deal
  async getSlackActivity(dealId: number): Promise<ApiResponse<{
    configured: boolean
    internal: any | null
    external: any | null
  }>> {
    return apiRequest(`/presales-dashboard/deals/${dealId}/slack/activity`)
  },

  // Sync Slack activity for a deal
  async syncSlackActivity(dealId: number): Promise<ApiResponse<{
    success: boolean
    synced: number
    errors?: string[]
  }>> {
    return apiRequest(`/presales-dashboard/deals/${dealId}/slack/sync`, {
      method: 'POST',
    })
  },

  // Post message to Slack
  async postSlackMessage(data: {
    channel: string
    text: string
  }): Promise<ApiResponse<{
    ok: boolean
    ts?: string
    error?: string
  }>> {
    return apiRequest('/presales-dashboard/slack/post-message', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // ===== Strategic Goal Tracking =====

  // Batch-fetch all goal metadata + latest sentiment
  async getGoalStates(): Promise<ApiResponse<Record<string, GoalState>>> {
    return apiRequest<Record<string, GoalState>>('/presales-dashboard/strategic-goals/states')
  },

  // Add sentiment entry (auto-save from slider)
  async addSentiment(goalId: string, data: { score: number; note?: string }): Promise<ApiResponse<{
    success: boolean
    latest_sentiment: { score: number; note: string | null; created_at: string } | null
  }>> {
    return apiRequest(`/presales-dashboard/strategic-goals/${goalId}/sentiment`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // Update goal status and/or due date
  async updateGoalMetadata(goalId: string, data: { status?: string; due_date?: string | null }): Promise<ApiResponse<{
    success: boolean
    metadata: { goal_id: string; status: string; due_date: string | null }
  }>> {
    return apiRequest(`/presales-dashboard/strategic-goals/${goalId}/metadata`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  // Full sentiment history for expanded view
  async getGoalHistory(goalId: string, params?: { limit?: number; offset?: number }): Promise<ApiResponse<{
    entries: SentimentEntry[]
    total: number
  }>> {
    const queryParams: Record<string, string> = {}
    if (params?.limit) queryParams.limit = params.limit.toString()
    if (params?.offset) queryParams.offset = params.offset.toString()
    return apiRequest(`/presales-dashboard/strategic-goals/${goalId}/history`, { params: queryParams })
  },

  // ===== Deal Actions =====

  async toggleDealAction(dealId: number, actionId: string, completed: boolean): Promise<ApiResponse<{ success: boolean; actionId: string; completed: boolean }>> {
    return apiRequest(`/presales-dashboard/deals/${dealId}/action`, {
      method: 'PATCH',
      body: JSON.stringify({ actionId, completed }),
    })
  },

  async markDealLost(dealId: number, lost: boolean, hubspotDealId?: string): Promise<ApiResponse<{ success: boolean; marked_lost: boolean }>> {
    return apiRequest(`/presales-dashboard/deals/${dealId}/mark-lost`, {
      method: 'PUT',
      body: JSON.stringify({ lost, hubspot_deal_id: hubspotDealId }),
    })
  },

  // ===== Presales Tasks =====

  async getTasks(dealId: number): Promise<ApiResponse<PresalesTask[]>> {
    return apiRequest<PresalesTask[]>(`/presales-dashboard/deals/${dealId}/tasks`)
  },

  async createTask(dealId: number | null, data: {
    template: string
    description: string
    context?: string
    assigned_to?: string
    due_date?: string
    jira_ticket?: string
    key_person?: string
    created_by?: string
    status_update?: string
    prospect_company?: string
    is_hard_deadline?: boolean
    is_critical_path?: boolean
  }): Promise<ApiResponse<PresalesTask>> {
    if (dealId) {
      // Deal-specific route (legacy — still works)
      return apiRequest<PresalesTask>(`/presales-dashboard/deals/${dealId}/tasks`, {
        method: 'POST',
        body: JSON.stringify(data),
      })
    }
    // Generic route for prospect/early-deal tasks
    return apiRequest<PresalesTask>(`/presales-dashboard/tasks`, {
      method: 'POST',
      body: JSON.stringify({ ...data, deal_id: null }),
    })
  },

  async toggleTaskStatus(taskId: number, data: {
    status: string
    completed_by?: string
  }): Promise<ApiResponse<PresalesTask>> {
    return apiRequest<PresalesTask>(`/presales-dashboard/tasks/${taskId}/status`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  async deleteTask(taskId: number): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest<{ success: boolean }>(`/presales-dashboard/tasks/${taskId}`, {
      method: 'DELETE',
    })
  },

  async updateTask(taskId: number, data: {
    description?: string
    assigned_to?: string | null
    due_date?: string | null
    context?: string | null
    jira_ticket?: string | null
    key_person?: string | null
    status_update?: string | null
    deal_id?: number | null
    prospect_company?: string | null
    performed_by?: string
    template?: string
    is_hard_deadline?: boolean
    is_critical_path?: boolean
  }): Promise<ApiResponse<PresalesTask>> {
    return apiRequest<PresalesTask>(`/presales-dashboard/tasks/${taskId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  async getTaskAuditLog(dealId: number): Promise<ApiResponse<PresalesTaskAuditEntry[]>> {
    return apiRequest<PresalesTaskAuditEntry[]>(`/presales-dashboard/deals/${dealId}/task-audit-log`)
  },

  async getTasksByAssignee(assignedTo: string): Promise<ApiResponse<PresalesTask[]>> {
    return apiRequest<PresalesTask[]>('/presales-dashboard/tasks', {
      params: { assigned_to: assignedTo },
    })
  },

  async getWaitingTasksWithJira(assignedTo: string): Promise<ApiResponse<WaitingJiraTask[]>> {
    return apiRequest<WaitingJiraTask[]>('/presales-dashboard/tasks/waiting-jira', {
      params: { assigned_to: assignedTo },
    })
  },

  async getCalendarTasks(): Promise<ApiResponse<(PresalesTask & { deal_name?: string; company_name?: string })[]>> {
    return apiRequest<(PresalesTask & { deal_name?: string; company_name?: string })[]>('/presales-dashboard/tasks', {
      params: { calendar: '1' },
    })
  },

  async getCustomWorkTasks(hubspotDealId: string): Promise<ApiResponse<PresalesTask[]>> {
    return apiRequest<PresalesTask[]>('/presales-dashboard/tasks', {
      params: { hubspot_deal_id: hubspotDealId },
    })
  },

  async getAllTasksByHubspotDealId(hubspotDealId: string): Promise<ApiResponse<PresalesTask[]>> {
    return apiRequest<PresalesTask[]>('/presales-dashboard/tasks', {
      params: { hubspot_deal_id: hubspotDealId, all: '1' },
    })
  },

  async getTaskAnalytics(): Promise<ApiResponse<TaskAnalyticsResult>> {
    return apiRequest<TaskAnalyticsResult>('/presales-dashboard/task-analytics')
  },

  async getHoursAnalytics(params?: { period?: string }): Promise<ApiResponse<HoursAnalyticsResult>> {
    const queryParams: Record<string, string> = {}
    if (params?.period) queryParams.period = params.period
    return apiRequest<HoursAnalyticsResult>('/presales-dashboard/hours-analytics', { params: queryParams })
  },

  async createCustomWorkTasks(data: {
    hubspot_deal_id: string
    custom_work_id: number
    custom_work_name: string
    custom_work_category: string
    custom_work_description?: string
    due_date?: string
    jira_ticket?: string
    assigned_pm_id?: number
    created_by?: string
  }): Promise<ApiResponse<{ pmTask: PresalesTask | null; executionTask: PresalesTask }>> {
    return apiRequest<{ pmTask: PresalesTask | null; executionTask: PresalesTask }>(
      '/presales-dashboard/custom-work-tasks',
      { method: 'POST', body: JSON.stringify(data) }
    )
  },
}

// =====================================================
// Slack API
// =====================================================
export const slackApi = {
  async postCalendar(data: {
    message: string
    channel?: string
    username?: string
  }): Promise<ApiResponse<{ success: boolean; message: string }>> {
    return apiRequest('/slack/calendar', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async getCalendarSettings(): Promise<ApiResponse<any>> {
    return apiRequest('/slack/calendar/settings', {
      method: 'GET',
    })
  },

  async updateCalendarSettings(settings: any): Promise<ApiResponse<any>> {
    return apiRequest('/slack/calendar/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    })
  },

  async sendTestNotification(): Promise<ApiResponse<{ success: boolean; message: string }>> {
    return apiRequest('/slack/calendar/test', {
      method: 'POST',
    })
  },

  async syncSlackUserIds(): Promise<ApiResponse<{ synced: number; failed: number; total: number; errors: string[] }>> {
    return apiRequest('/slack/calendar/sync-user-ids', {
      method: 'POST',
    })
  },

  async listChannels(): Promise<ApiResponse<any[]>> {
    return apiRequest('/slack/channels', {
      method: 'GET',
    })
  },

  // ── Multi-notification endpoints ──────────────────────────────────────
  async getNotifications(): Promise<ApiResponse<any[]>> {
    return apiRequest('/slack/notifications', { method: 'GET' })
  },

  async getNotification(id: number): Promise<ApiResponse<any>> {
    return apiRequest(`/slack/notifications/${id}`, { method: 'GET' })
  },

  async updateNotification(id: number, data: any): Promise<ApiResponse<any>> {
    return apiRequest(`/slack/notifications/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  async testNotification(id: number): Promise<ApiResponse<any>> {
    return apiRequest(`/slack/notifications/${id}/test`, { method: 'POST' })
  },

  // force: run even when the notification is disabled — for a deliberate
  // "send now", as opposed to a scheduled job, which should stay silent.
  async triggerNotification(id: number, channel?: string, force?: boolean): Promise<ApiResponse<any>> {
    const query = new URLSearchParams()
    if (channel) query.set('channel', channel)
    if (force) query.set('force', '1')
    const params = query.toString() ? `?${query.toString()}` : ''
    return apiRequest(`/slack/notifications/${id}/trigger${params}`, { method: 'POST' })
  },

  async previewNotification(id: number): Promise<ApiResponse<{ html: string }>> {
    return apiRequest(`/slack/notifications/${id}/preview`, { method: 'GET' })
  },
}

// =====================================================
// Product Managers API
// =====================================================
export const productManagersApi = {
  async list(): Promise<ApiResponse<{ items: any[] }>> {
    return apiRequest('/product-managers', {
      method: 'GET',
    })
  },

  async get(id: number): Promise<ApiResponse<any>> {
    return apiRequest(`/product-managers/${id}`, {
      method: 'GET',
    })
  },

  async create(pm: { name: string; email: string; slack_user_id?: string | null; slack_username?: string | null }): Promise<ApiResponse<any>> {
    return apiRequest('/product-managers', {
      method: 'POST',
      body: JSON.stringify(pm),
    })
  },

  async update(id: number, pm: any): Promise<ApiResponse<any>> {
    return apiRequest(`/product-managers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(pm),
    })
  },

  async deactivate(id: number): Promise<ApiResponse<{ success: boolean; message: string }>> {
    return apiRequest(`/product-managers/${id}`, {
      method: 'DELETE',
    })
  },
}

// =====================================================
// TEAM MEMBERS API
// =====================================================

export interface TeamMember {
  id: number
  name: string
  email: string
  role: string | null
  team: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export const teamMembersApi = {
  async list(team?: string): Promise<ApiResponse<{ items: TeamMember[] }>> {
    const qs = team ? `?team=${encodeURIComponent(team)}` : ''
    return apiRequest(`/team-members${qs}`, { method: 'GET' })
  },

  async create(member: { name: string; email: string; role?: string | null; team?: string | null }): Promise<ApiResponse<TeamMember>> {
    return apiRequest('/team-members', {
      method: 'POST',
      body: JSON.stringify(member),
    })
  },

  async update(id: number, member: Partial<Pick<TeamMember, 'name' | 'email' | 'role' | 'team' | 'is_active'>>): Promise<ApiResponse<TeamMember>> {
    return apiRequest(`/team-members/${id}`, {
      method: 'PUT',
      body: JSON.stringify(member),
    })
  },

  async deactivate(id: number): Promise<ApiResponse<{ success: boolean; message: string }>> {
    return apiRequest(`/team-members/${id}`, {
      method: 'DELETE',
    })
  },
}

// =====================================================
// JIRA API (Implementations Dashboard)
// =====================================================
export interface JiraTicketStatusResult {
  key: string
  summary: string
  status: string
  statusCategory: string // 'new' | 'indeterminate' | 'done'
}

const jiraApi = {
  async getInitiatives(): Promise<ApiResponse<JiraSearchResponse>> {
    return apiRequest<JiraSearchResponse>('/jira/initiatives')
  },

  async getGoLives(quarter: string, year: number): Promise<ApiResponse<{ count: number; issues: Array<{ key: string; summary: string; status: string; assignee: string | null }> }>> {
    return apiRequest('/jira/golives', { params: { quarter, year: year.toString() } })
  },

  async prepareHuddle(
    tickets: string[]
  ): Promise<ApiResponse<{ success: boolean; updated: string[]; errors: { key: string; error: string }[] }>> {
    return apiRequest<{ success: boolean; updated: string[]; errors: { key: string; error: string }[] }>(
      '/jira/prepare-huddle',
      {
        method: 'POST',
        body: JSON.stringify({ tickets }),
      }
    )
  },

  async fetchPresalesTicketStatuses(
    tickets: string[]
  ): Promise<ApiResponse<{ success: boolean; statuses: Record<string, JiraTicketStatusResult> }>> {
    return apiRequest<{ success: boolean; statuses: Record<string, JiraTicketStatusResult> }>(
      '/jira/presales-ticket-status',
      {
        method: 'POST',
        body: JSON.stringify({ tickets }),
      }
    )
  },

  async listDurationOverrides(): Promise<ApiResponse<{ overrides: DurationOverride[] }>> {
    return apiRequest<{ overrides: DurationOverride[] }>('/jira/duration-overrides')
  },

  async createDurationOverride(
    override: Omit<DurationOverride, 'id' | 'created_at'>
  ): Promise<ApiResponse<{ id: number }>> {
    return apiRequest<{ id: number }>('/jira/duration-overrides', {
      method: 'POST',
      body: JSON.stringify(override),
    })
  },

  async deleteDurationOverride(id: number): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest<{ success: boolean }>(`/jira/duration-overrides/${id}`, { method: 'DELETE' })
  },
}

// =====================================================
// IMPL BOT API (manual triggers for weekly review cycle)
// =====================================================
export interface ImplCommentHistoryEntry {
  id: number
  comment: string
  author_email: string
  author_name: string
  created_at: string
}

export const implBotApi = {
  async runThursday(comments?: Record<string, string>): Promise<ApiResponse<{ success: boolean; job: string; status: string }>> {
    return apiRequest('/impl-bot/thursday', {
      method: 'POST',
      body: JSON.stringify({ comments }),
      headers: { 'Content-Type': 'application/json' },
    })
  },
  async runFriday(): Promise<ApiResponse<{ success: boolean; job: string; status: string }>> {
    return apiRequest('/impl-bot/friday', { method: 'POST' })
  },
  async runMonday(): Promise<ApiResponse<{ success: boolean; job: string; status: string }>> {
    return apiRequest('/impl-bot/monday', { method: 'POST' })
  },
  async deleteLastPost(): Promise<ApiResponse<{ success: boolean; message: string }>> {
    return apiRequest('/impl-bot/delete-last-post', { method: 'POST' })
  },
  async getComments(): Promise<ApiResponse<{ comments: Record<string, string> }>> {
    return apiRequest('/impl-bot/comments')
  },
  async getCommentHistory(jiraKey: string): Promise<ApiResponse<{ history: ImplCommentHistoryEntry[] }>> {
    return apiRequest(`/impl-bot/comments/${encodeURIComponent(jiraKey)}/history`)
  },
  async saveComment(jiraKey: string, comment: string): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest(`/impl-bot/comments/${encodeURIComponent(jiraKey)}`, {
      method: 'PUT',
      body: JSON.stringify({ comment }),
      headers: { 'Content-Type': 'application/json' },
    })
  },
  async getPauseReasons(): Promise<ApiResponse<{ reasons: Record<string, string> }>> {
    return apiRequest('/impl-bot/pause-reasons')
  },
  async savePauseReason(jiraKey: string, reason: string): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest(`/impl-bot/pause-reasons/${encodeURIComponent(jiraKey)}`, {
      method: 'PUT',
      body: JSON.stringify({ reason }),
      headers: { 'Content-Type': 'application/json' },
    })
  },
}

// =====================================================
// HELP DOC TEMPLATES API
// =====================================================
export interface HelpDocTemplate {
  id: number
  user_email: string
  user_name: string | null
  api_version: string
  section_id: string
  name: string
  html: string
  created_at: string
  updated_at: string
}

export const helpDocTemplatesApi = {
  async list(version?: string, section?: string): Promise<ApiResponse<HelpDocTemplate[]>> {
    const params: Record<string, string> = {}
    if (version) params.version = version
    if (section) params.section = section
    return apiRequest('/help-doc-templates', { params })
  },

  async save(template: { apiVersion: string; sectionId: string; name: string; html: string }): Promise<ApiResponse<HelpDocTemplate>> {
    return apiRequest('/help-doc-templates', {
      method: 'POST',
      body: JSON.stringify(template),
    })
  },

  async remove(id: number): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest(`/help-doc-templates/${id}`, { method: 'DELETE' })
  },
}

// =====================================================
// SOW Template API
// =====================================================
export type SowTemplateType = 'global' | 'upsell' | 'reseller_parent' | 'reseller_child'

export const sowTemplateApi = {
  async get(type: SowTemplateType = 'global'): Promise<ApiResponse<{ config: Record<string, unknown>; updatedBy: string | null; updatedByName: string | null; updatedAt: string | null }>> {
    return apiRequest(`/sow-template?type=${type}`)
  },
  async save(config: Record<string, unknown>, type: SowTemplateType = 'global'): Promise<ApiResponse<{ config: Record<string, unknown>; updatedBy: string | null; updatedByName: string | null; updatedAt: string | null }>> {
    return apiRequest(`/sow-template?type=${type}`, { method: 'PUT', body: JSON.stringify({ config }) })
  },
}

// =====================================================
// Sequence Diagram Library API
// =====================================================
export interface SequenceDiagramEntry {
  id: number
  name: string
  description: string | null
  dsl: string
  createdByName: string | null
  updatedByName: string | null
  updatedAt: string
}

export const sequenceDiagramLibraryApi = {
  async list(): Promise<ApiResponse<SequenceDiagramEntry[]>> {
    return apiRequest<SequenceDiagramEntry[]>('/sequence-diagram-library')
  },
  async create(data: { name: string; description?: string; dsl: string }): Promise<ApiResponse<{ id: number }>> {
    return apiRequest<{ id: number }>('/sequence-diagram-library', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },
  async update(id: number, data: { name?: string; description?: string; dsl?: string }): Promise<ApiResponse<{ ok: true }>> {
    return apiRequest<{ ok: true }>(`/sequence-diagram-library/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },
  async remove(id: number): Promise<ApiResponse<{ ok: true }>> {
    return apiRequest<{ ok: true }>(`/sequence-diagram-library/${id}`, {
      method: 'DELETE',
    })
  },
  async generate(prompt: string): Promise<ApiResponse<{ dsl: string }>> {
    return apiRequest<{ dsl: string }>('/sequence-diagram-library/generate', {
      method: 'POST',
      body: JSON.stringify({ prompt }),
    })
  },
}

// =====================================================
// Client Projects API
// =====================================================
export interface ClientProject {
  id: number
  customerName: string
  owner: string
  region: string
  stage: string
  stageEnteredAt: string
  targetGoLive: string | null
  notes: string | null
  archived: boolean
  createdAt: string
  updatedAt: string
}

export interface ClientProjectPhaseItem {
  id: number
  type: string
  title: string
  status: string
  notes: string | null
  tutorial: string | null
  startedAt: string | null
  blockedSince: string | null
  blockedBy: string | null
  updatedAt: string
  sortOrder?: number
  subitems?: ClientProjectPhaseItem[]
}

export interface ClientProjectPhase {
  n: number
  title: string
  items: ClientProjectPhaseItem[]
}

export interface ClientProjectDetail extends ClientProject {
  phases: ClientProjectPhase[]
}

export interface ProjectProgressEntry {
  id: number
  project_id: number
  type: 'update' | 'todo'
  text: string
  done: boolean
  created_at: string
}

export const clientProjectsApi = {
  async list(): Promise<ApiResponse<ClientProject[]>> {
    return apiRequest<ClientProject[]>('/client-projects')
  },
  async get(id: number): Promise<ApiResponse<ClientProjectDetail>> {
    return apiRequest<ClientProjectDetail>(`/client-projects/${id}`)
  },
  async create(data: { customerName: string; owner: string; region?: string; targetGoLive?: string }): Promise<ApiResponse<{ id: number }>> {
    return apiRequest<{ id: number }>('/client-projects', { method: 'POST', body: JSON.stringify(data) })
  },
  async update(id: number, data: Partial<{ customerName: string; owner: string; region: string; stage: string; targetGoLive: string | null; notes: string | null; archived: boolean }>): Promise<ApiResponse<{ success: true }>> {
    return apiRequest<{ success: true }>(`/client-projects/${id}`, { method: 'PATCH', body: JSON.stringify(data) })
  },
  async updateItem(itemId: number, data: { status?: string; notes?: string | null; title?: string; tutorial?: string | null; blockedBy?: string | null }): Promise<ApiResponse<{ success: true }>> {
    return apiRequest<{ success: true }>(`/client-projects/items/${itemId}`, { method: 'PATCH', body: JSON.stringify(data) })
  },
  async deleteItem(itemId: number): Promise<ApiResponse<{ success: true }>> {
    return apiRequest<{ success: true }>(`/client-projects/items/${itemId}`, { method: 'DELETE' })
  },
  async addItem(projectId: number, data: { phaseNumber: number; phaseTitle: string; title: string; parentItemId?: number }): Promise<ApiResponse<{ id: number }>> {
    return apiRequest<{ id: number }>(`/client-projects/${projectId}/items`, { method: 'POST', body: JSON.stringify(data) })
  },
  async addPhase(projectId: number, title: string): Promise<ApiResponse<{ phaseNumber: number; firstItemId: number }>> {
    return apiRequest<{ phaseNumber: number; firstItemId: number }>(`/client-projects/${projectId}/phases`, { method: 'POST', body: JSON.stringify({ title }) })
  },
  async renamePhase(projectId: number, phaseNumber: number, title: string): Promise<ApiResponse<{ success: true }>> {
    return apiRequest<{ success: true }>(`/client-projects/${projectId}/phases/${phaseNumber}`, { method: 'PATCH', body: JSON.stringify({ title }) })
  },
  async deletePhase(projectId: number, phaseNumber: number): Promise<ApiResponse<{ success: true }>> {
    return apiRequest<{ success: true }>(`/client-projects/${projectId}/phases/${phaseNumber}`, { method: 'DELETE' })
  },
  async remove(id: number): Promise<ApiResponse<{ success: true }>> {
    return apiRequest<{ success: true }>(`/client-projects/${id}`, { method: 'DELETE' })
  },
  async listProgress(): Promise<ApiResponse<ProjectProgressEntry[]>> {
    return apiRequest<ProjectProgressEntry[]>('/client-projects/progress')
  },
  async addProgress(projectId: number, type: 'update' | 'todo', text: string): Promise<ApiResponse<ProjectProgressEntry>> {
    return apiRequest<ProjectProgressEntry>(`/client-projects/${projectId}/progress`, {
      method: 'POST', body: JSON.stringify({ type, text }),
    })
  },
  async patchProgress(entryId: number, data: { done?: boolean; text?: string; created_at?: string }): Promise<ApiResponse<{ success: true }>> {
    return apiRequest<{ success: true }>(`/client-projects/progress/${entryId}`, {
      method: 'PATCH', body: JSON.stringify(data),
    })
  },
  async deleteProgress(entryId: number): Promise<ApiResponse<{ success: true }>> {
    return apiRequest<{ success: true }>(`/client-projects/progress/${entryId}`, { method: 'DELETE' })
  },
}

// =====================================================
// Platform Journey Prototype API — saved new-customer walkthroughs
// =====================================================
export interface PlatformJourneySummary {
  id: number
  customerName: string
  version: string
  createdAt: string
  updatedAt: string
}

// Stage config/checklist blobs are opaque JSON on the wire — PlatformJourneyTool
// owns their actual shapes and parses/builds them itself.
export interface PlatformJourneyRecord extends PlatformJourneySummary {
  accountId: string | null
  country: string
  portalUserName: string | null
  portalUserEmail: string | null
  notes: string | null
  selectedEnvs: unknown[]
  envAccounts: unknown[]
  workflowConfig: unknown | null
  idvConfig: unknown | null
  idvChecklist: unknown | null
  kycConfig: unknown | null
  kycChecklist: unknown | null
  kybConfig: unknown | null
  kybChecklist: unknown | null
  amlConfig: unknown | null
  amlChecklist: unknown | null
  fraudConfig: unknown | null
  fraudChecklist: unknown | null
  tmConfig: unknown | null
  tmChecklist: unknown | null
  createdBy: string | null
  createdByName: string | null
}

export type PlatformJourneyUpdate = Partial<Omit<PlatformJourneyRecord, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'createdByName'>>

export const platformJourneysApi = {
  async list(): Promise<ApiResponse<PlatformJourneySummary[]>> {
    return apiRequest<PlatformJourneySummary[]>('/platform-journeys')
  },
  async get(id: number): Promise<ApiResponse<PlatformJourneyRecord>> {
    return apiRequest<PlatformJourneyRecord>(`/platform-journeys/${id}`)
  },
  async create(data: { customerName: string } & Partial<PlatformJourneyUpdate>): Promise<ApiResponse<{ id: number }>> {
    return apiRequest<{ id: number }>('/platform-journeys', { method: 'POST', body: JSON.stringify(data) })
  },
  async update(id: number, data: PlatformJourneyUpdate): Promise<ApiResponse<{ success: true }>> {
    return apiRequest<{ success: true }>(`/platform-journeys/${id}`, { method: 'PUT', body: JSON.stringify(data) })
  },
  async remove(id: number): Promise<ApiResponse<{ success: true }>> {
    return apiRequest<{ success: true }>(`/platform-journeys/${id}`, { method: 'DELETE' })
  },
}

// =====================================================
// Platform Journey — AI Configure from Scope
// =====================================================
import type { AiChatMessage, AiChatResponse, AiConfigureSuggestions, AiObjectives } from '@features/dashboards/components/PlatformJourneyTool/aiConfigure'

export const platformJourneyAiApi = {
  // Read BEFORE any per-stage suggestion is drafted — infers what this
  // customer is optimising for (pass rate, cost, speed, latency, fraud
  // controls, compliance posture) and which jurisdictions they operate in,
  // so the rep can review/edit it before it's used to draft anything else.
  async inferObjectives(scopeData: unknown) {
    return apiRequest<AiObjectives>(
      '/platform-journey-ai/objectives',
      { method: 'POST', body: JSON.stringify({ scopeData }) },
    )
  },
  // `objectives` is optional so a straight re-generate without the
  // objectives step still works — passing it threads the rep-confirmed
  // priorities into the model's vendor/cascade reasoning.
  async configureFromScope(scopeData: unknown, objectives?: AiObjectives | null) {
    return apiRequest<AiConfigureSuggestions>(
      '/platform-journey-ai/configure-from-scope',
      { method: 'POST', body: JSON.stringify({ scopeData, objectives }) },
    )
  },
  // Follow-up conversation about an already-generated batch of suggestions —
  // `suggestions` and `includedStages` are the current state the rep is
  // looking at, so the model can answer/adjust in context rather than
  // re-deriving everything from scopeData alone.
  async chat(payload: {
    scopeData: unknown
    objectives?: AiObjectives | null
    suggestions: AiConfigureSuggestions
    includedStages: string[]
    messages: AiChatMessage[]
  }) {
    return apiRequest<AiChatResponse>(
      '/platform-journey-ai/chat',
      { method: 'POST', body: JSON.stringify(payload) },
    )
  },
}

// =====================================================
// POC Manager API
// =====================================================
export const pocApi = {
  async list(params?: { status?: string; deal_id?: string }) {
    return apiRequest<unknown[]>('/poc', { params: params as Record<string, string> })
  },
  async get(id: number) {
    return apiRequest<unknown>(`/poc/${id}`)
  },
  async create(data: { deal_name: string; company_name?: string; presales_deal_id?: number; created_by?: string; created_by_name?: string; presales_owner?: string }) {
    return apiRequest<unknown>('/poc', { method: 'POST', body: JSON.stringify(data) })
  },
  async update(id: number, data: Record<string, unknown>) {
    return apiRequest<unknown>(`/poc/${id}`, { method: 'PUT', body: JSON.stringify(data) })
  },
  async remove(id: number) {
    return apiRequest<{ deleted: boolean }>(`/poc/${id}`, { method: 'DELETE' })
  },
  async uploadResults(id: number, rows: { customer_record_id: string; country?: string; vendor_results: Record<string, unknown> }[]) {
    return apiRequest<{ imported: number }>(`/poc/${id}/results/upload`, { method: 'POST', body: JSON.stringify({ rows }) })
  },
  async getResults(id: number) {
    return apiRequest<unknown[]>(`/poc/${id}/results`)
  },
  async clearResults(id: number) {
    return apiRequest<{ cleared: boolean }>(`/poc/${id}/results`, { method: 'DELETE' })
  },
  async calculate(id: number) {
    return apiRequest<unknown>(`/poc/${id}/calculate`, { method: 'POST' })
  },
  async refreshJiraStatus(id: number) {
    return apiRequest<unknown[]>(`/poc/${id}/jira-status`, { method: 'POST' })
  },
  async aiAssist(id: number, data: { notes?: string; transcript_ids?: number[]; include_excerpt?: boolean }) {
    return apiRequest<{ suggestions: Record<string, { value: string | null; confidence: string; source: string }> }>(`/poc/${id}/ai-assist`, { method: 'POST', body: JSON.stringify(data) })
  },
  async generateVendorMessage(data: { vendor: string; company: string; countries: unknown[]; vendor_sources?: string; additional_context?: string }) {
    return apiRequest<{ message: string }>('/poc/generate-vendor-message', { method: 'POST', body: JSON.stringify(data) })
  },
  async generateConsiderations(id: number, prompt?: string) {
    return apiRequest<{ status: string }>(`/poc/${id}/considerations`, {
      method: 'POST',
      body: JSON.stringify(prompt ? { prompt } : {}),
    })
  },
  async getConsiderationsStatus(id: number) {
    return apiRequest<{ status: string; considerations: string | null; error: string | null }>(`/poc/${id}/considerations/status`)
  },
  async startReminders(id: number) {
    return apiRequest<{ action: string; message_posted: boolean; slack_error?: string }>(`/poc/${id}/reminders`, {
      method: 'POST',
      body: JSON.stringify({ action: 'start' }),
    })
  },
  async stopReminders(id: number) {
    return apiRequest<{ action: string }>(`/poc/${id}/reminders`, {
      method: 'POST',
      body: JSON.stringify({ action: 'stop' }),
    })
  },
  async getSlackMessages(id: number) {
    return apiRequest<Array<{ id: number; poc_id: number; message_type: string; slack_ts: string | null; channel_id: string; message_text: string; people_tagged: string | null; created_at: string }>>(`/poc/${id}/slack-messages`)
  },
  async resendSlackMessage(messageId: number) {
    return apiRequest<{ resent: boolean; slack_ts: string | null }>(`/poc/slack-messages/${messageId}/resend`, { method: 'POST' })
  },
  async postStageUpdate(id: number, message: string) {
    return apiRequest<{ posted: boolean; slack_ts: string | null }>(`/poc/${id}/slack-stage-update`, { method: 'POST', body: JSON.stringify({ message }) })
  },
  async createCanvas(id: number) {
    return apiRequest<{ canvas_id: string }>(`/poc/${id}/canvas`, { method: 'POST' })
  },
  async loadPassRates(id: number) {
    return apiRequest<{ rates: Record<string, number | null>; raw: unknown[] }>(`/poc/${id}/pass-rates`, { method: 'POST' })
  },
  async lookupDeal(hubspotId: string, dealName?: string, companyName?: string) {
    const params: Record<string, string> = { hubspot_id: hubspotId }
    if (dealName) params.deal_name = dealName
    if (companyName) params.company_name = companyName
    return apiRequest<{ presales_deal_id: number | null; scope_deal_id: number | null }>(`/poc/lookup-deal`, { params })
  },
}

// =====================================================
// Pipeline and Products weekly meetings API
// =====================================================
export interface PipelineProductMeetingSummary {
  id: number
  week_start: string
  general_notes: string
  created_by: string | null
  created_at: string
  updated_at: string
}

export interface PipelineProductAgendaItem {
  id: number
  item_type: 'scope' | 'custom_work'
  scope_id: number
  custom_work_id: number | null
  notes: string
  sort_order: number
  discussed: boolean
  discussed_at: string | null
  confirmed_standard: boolean
  confirmed_standard_at: string | null
  added_by: string | null
  added_at: string
  updated_at: string
  deal_id: number | null
  deal_name: string
  customer_name: string | null
  hubspot_deal_id: string | null
  custom_work_name?: string
  custom_work_description?: string
  custom_work_category?: string
  custom_work_due_date?: string
  custom_work_effort_days?: number | null
  custom_work_lifecycle_status?: string
  // Raw JSON blobs from the scope (for TLDR rendering — only present on scope items)
  general_info?: string | null
  kyc_config?: string | null
  aml_config?: string | null
  biometrics_config?: string | null
  kyb_config?: string | null
  rules_config?: string | null
}

export interface PipelineProductMeeting extends PipelineProductMeetingSummary {
  items: PipelineProductAgendaItem[]
}

export interface ScopeSearchResult {
  scope_id: number
  deal_id: number
  deal_name: string
  customer_name: string | null
  hubspot_deal_id: string | null
  updated_at: string
}

export interface CustomWorkSearchResult {
  scope_id: number
  deal_id: number
  deal_name: string
  customer_name: string | null
  hubspot_deal_id: string | null
  custom_work_id: number
  name: string
  description: string
  category: string
  due_date: string
  effort_days: number | null
  lifecycle_status: string
}

const pipelineProductsApi = {
  async listMeetings(): Promise<ApiResponse<{ meetings: PipelineProductMeetingSummary[] }>> {
    return apiRequest('/pipeline-products/meetings')
  },
  async getMeetingByWeek(weekStart: string): Promise<ApiResponse<PipelineProductMeeting>> {
    return apiRequest(`/pipeline-products/meetings/${weekStart}`)
  },
  async updateMeetingNotes(id: number, general_notes: string): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest(`/pipeline-products/meetings/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ general_notes }),
    })
  },
  async addAgendaItem(
    meetingId: number,
    payload: { item_type: 'scope' | 'custom_work'; scope_id?: number; deal_id?: number; custom_work_id?: number | null }
  ): Promise<ApiResponse<{ id: number; success: boolean }>> {
    return apiRequest(`/pipeline-products/meetings/${meetingId}/items`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },
  async updateItem(id: number, payload: { notes?: string; sort_order?: number; discussed?: boolean; confirmed_standard?: boolean }): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest(`/pipeline-products/items/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
  },
  async deleteItem(id: number): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest(`/pipeline-products/items/${id}`, { method: 'DELETE' })
  },
  async searchScopes(q: string): Promise<ApiResponse<{ results: ScopeSearchResult[] }>> {
    return apiRequest(`/pipeline-products/search-scopes?q=${encodeURIComponent(q)}`)
  },
  async searchCustomWorks(q: string): Promise<ApiResponse<{ results: CustomWorkSearchResult[] }>> {
    return apiRequest(`/pipeline-products/search-custom-works?q=${encodeURIComponent(q)}`)
  },
}

// =====================================================
// Coverage Import API
// =====================================================
export const coverageApi = {
  async importCountries(payload: { source_file: string; countries: unknown[] }) {
    return apiRequest<{ imported: number }>('/coverage/import', { method: 'POST', body: JSON.stringify(payload) })
  },
  async importSources(payload: { source_file: string; sources: unknown[] }) {
    return apiRequest<{ imported: number }>('/coverage/sources/import', { method: 'POST', body: JSON.stringify(payload) })
  },
  async importRecommendations(payload: { source_file: string; recommendations: unknown[] }) {
    return apiRequest<{ imported: number }>('/coverage/recommendations/import', { method: 'POST', body: JSON.stringify(payload) })
  },
  async stats() {
    const r = await apiRequest<{ success: boolean; data: {
      countries: { count: number; last_imported: string | null; source_file: string | null }
      sources: { count: number; last_imported: string | null }
      recommendations: { count: number; last_imported: string | null }
    } }>('/coverage/stats')
    return { success: r.success, data: (r.data as any)?.data, error: r.error }
  },
  async buildUnified() {
    const r = await apiRequest<{ success: boolean; data: { rows: number } }>('/coverage/build-unified', { method: 'POST' })
    return { success: r.success, data: (r.data as any)?.data, error: r.error }
  },
  async getNationalIds(params?: { country?: string; vendor?: string }) {
    return apiRequest<unknown[]>('/coverage/national-ids', { params: params as Record<string, string> })
  },
  async importNationalIds(payload: { vendor: string; source_file?: string; rows: unknown[] }) {
    return apiRequest<{ imported: number }>('/coverage/national-ids/import', { method: 'POST', body: JSON.stringify(payload) })
  },
  async getInsights(params?: { country?: string; vendor?: string }) {
    return apiRequest<unknown[]>('/coverage/insights', { params: params as Record<string, string> })
  },
  async createInsight(data: { country_code?: string; vendor?: string; data_source_header?: string; severity?: string; title: string; detail: string; tags?: string[]; created_by?: string; created_by_name?: string }) {
    return apiRequest<{ id: number }>('/coverage/insights', { method: 'POST', body: JSON.stringify(data) })
  },
  async deleteInsight(id: number) {
    return apiRequest<{ deleted: boolean }>(`/coverage/insights/${id}`, { method: 'DELETE' })
  },
  async previewUnified(opts: { limit?: number; countries?: string[] } = {}) {
    const params: Record<string, string> = {}
    if (opts.countries && opts.countries.length > 0) params.countries = opts.countries.join(',')
    else if (opts.limit) params.limit = String(opts.limit)
    const r = await apiRequest<{ success: boolean; data: unknown[] }>('/coverage/preview-unified', { params })
    return { success: r.success, data: (r.data as any)?.data, error: r.error }
  },
}

// =====================================================
// Country Guides API (AI-generated verification guides)
// =====================================================
export interface CountryGuideProfile {
  id: number
  country_code: string
  country_name: string
  custom_prompt?: string | null
  generated_content?: string | null
  status: 'pending' | 'generating' | 'generated' | 'error'
  generated_at?: string | null
  generated_by?: string | null
  generated_by_name?: string | null
  content_length?: number
  has_custom_prompt?: number
  created_at: string
  updated_at: string
}

export const pocCountryTemplatesApi = {
  async list() {
    return apiRequest<{ id: number; country_code: string; country_name: string; label: string; fields_json: string; updated_at: string }[]>('/poc/country-templates')
  },
  async get(code: string) {
    return apiRequest<{ id: number; country_code: string; country_name: string; label: string; fields_json: string; updated_at: string } | null>(`/poc/country-templates/${code}`)
  },
  async save(code: string, data: { country_name: string; fields_json: unknown[]; label?: string; created_by?: string; created_by_name?: string }) {
    return apiRequest<{ success: boolean }>(`/poc/country-templates/${code}`, { method: 'PUT', body: JSON.stringify(data) })
  },
  async remove(code: string) {
    return apiRequest<{ success: boolean }>(`/poc/country-templates/${code}`, { method: 'DELETE' })
  },
}

export const countryGuidesApi = {
  async list() {
    return apiRequest<CountryGuideProfile[]>('/country-guides')
  },
  async get(code: string) {
    return apiRequest<CountryGuideProfile>(`/country-guides/${code}`)
  },
  async create(country_code: string, country_name: string) {
    return apiRequest<{ id: number }>('/country-guides', { method: 'POST', body: JSON.stringify({ country_code, country_name }) })
  },
  async updatePrompt(code: string, custom_prompt: string | null) {
    return apiRequest<{ updated: boolean }>(`/country-guides/${code}`, { method: 'PUT', body: JSON.stringify({ custom_prompt }) })
  },
  async delete(code: string) {
    return apiRequest<{ deleted: boolean }>(`/country-guides/${code}`, { method: 'DELETE' })
  },
  async generate(code: string, generatedBy?: string, generatedByName?: string) {
    return apiRequest<{ content: string; status: string }>(`/country-guides/${code}/generate`, {
      method: 'POST',
      body: JSON.stringify({ generated_by: generatedBy, generated_by_name: generatedByName }),
    })
  },
  async getDefaultPrompt() {
    return apiRequest<{ prompt: string }>('/country-guides/default-prompt')
  },
}

// =====================================================
// Vendor API Docs (link registry)
// =====================================================
export interface VendorApiVendor {
  id: number
  slug: string
  name: string
  docs_base_url?: string | null
  link_count?: number
  source_count?: number
  latest_link_at?: string | null
  latest_import_at?: string | null
  created_at: string
  updated_at: string
}

export interface VendorApiLink {
  id: number
  vendor_id: number
  vendor_name?: string
  vendor_slug?: string
  country_code: string
  country_name: string
  service_name: string
  doc_url: string
  notes?: string | null
  created_by_name?: string | null
  created_at: string
  updated_at: string
}

export interface VendorSourceReport {
  id: number
  vendor_id: number
  vendor_name?: string
  country_name: string
  country_code?: string | null
  source_name: string
  source_type?: string | null
  description?: string | null
  adult_coverage?: string | null
  refresh_frequency?: string | null
  required_fields?: string | null
  verifiable_elements?: string | null
  consent_required?: string | null
  credit_footprint?: string | null
  external_notes?: string | null
  source_last_updated?: string | null
  created_at: string
}

export const vendorApiDocsApi = {
  async listVendors() {
    return apiRequest<VendorApiVendor[]>('/vendor-api-docs/vendors')
  },
  async createVendor(data: { name: string; slug: string; docs_base_url?: string }) {
    return apiRequest<{ id: number }>('/vendor-api-docs/vendors', { method: 'POST', body: JSON.stringify(data) })
  },
  async updateVendor(id: number, data: { name?: string; docs_base_url?: string }) {
    return apiRequest<{ updated: boolean }>(`/vendor-api-docs/vendors/${id}`, { method: 'PUT', body: JSON.stringify(data) })
  },
  async deleteVendor(id: number) {
    return apiRequest<{ deleted: boolean }>(`/vendor-api-docs/vendors/${id}`, { method: 'DELETE' })
  },
  async listLinks(params?: { vendor_id?: number; country_code?: string }) {
    const p: Record<string, string> = {}
    if (params?.vendor_id) p.vendor_id = String(params.vendor_id)
    if (params?.country_code) p.country_code = params.country_code
    return apiRequest<VendorApiLink[]>('/vendor-api-docs/links', { params: p })
  },
  async createLink(data: { vendor_id: number; country_code: string; country_name: string; service_name: string; doc_url: string; notes?: string; created_by_name?: string }) {
    return apiRequest<{ id: number }>('/vendor-api-docs/links', { method: 'POST', body: JSON.stringify(data) })
  },
  async updateLink(id: number, data: { service_name?: string; doc_url?: string; notes?: string }) {
    return apiRequest<{ updated: boolean }>(`/vendor-api-docs/links/${id}`, { method: 'PUT', body: JSON.stringify(data) })
  },
  async deleteLink(id: number) {
    return apiRequest<{ deleted: boolean }>(`/vendor-api-docs/links/${id}`, { method: 'DELETE' })
  },
  async bulkImport(data: { vendor_id: number; links: { country_code: string; country_name: string; service_name: string; doc_url: string }[]; created_by_name?: string }) {
    return apiRequest<{ imported: number }>('/vendor-api-docs/links/bulk', { method: 'POST', body: JSON.stringify(data) })
  },
  async listSourceReports(params?: { vendor_id?: number; country_code?: string; search?: string }) {
    const p: Record<string, string> = {}
    if (params?.vendor_id) p.vendor_id = String(params.vendor_id)
    if (params?.country_code) p.country_code = params.country_code
    if (params?.search) p.search = params.search
    return apiRequest<VendorSourceReport[]>('/vendor-api-docs/source-reports', { params: p })
  },
  async importSourceReports(data: { vendor_id: number; source_file?: string; imported_by_name?: string; rows: Partial<VendorSourceReport>[] }) {
    return apiRequest<{ imported: number }>('/vendor-api-docs/source-reports/import', { method: 'POST', body: JSON.stringify(data) })
  },
  async sourceReportImportInfo(vendorId: number) {
    return apiRequest<{ import: unknown; stats: { total: number; countries: number } }>('/vendor-api-docs/source-reports/import-info', { params: { vendor_id: String(vendorId) } })
  },
}

// =====================================================
// Match Rate Enquiry API
// =====================================================
export const matchRateApi = {
  async list() {
    return apiRequest<unknown[]>('/match-rate')
  },
  async get(id: number) {
    return apiRequest<unknown>(`/match-rate/${id}`)
  },
  async create(data: { title?: string; prompt?: string; created_by?: string; created_by_name?: string }) {
    return apiRequest<unknown>('/match-rate', { method: 'POST', body: JSON.stringify(data) })
  },
  async update(id: number, data: Record<string, unknown>) {
    return apiRequest<unknown>(`/match-rate/${id}`, { method: 'PUT', body: JSON.stringify(data) })
  },
  async remove(id: number) {
    return apiRequest<{ deleted: boolean }>(`/match-rate/${id}`, { method: 'DELETE' })
  },
  async parse(id: number) {
    return apiRequest<unknown>(`/match-rate/${id}/parse`, { method: 'POST' })
  },
  async analyse(id: number) {
    return apiRequest<unknown>(`/match-rate/${id}/analyse`, { method: 'POST' })
  },
  async ask(data: { question: string; created_by?: string; created_by_name?: string }) {
    return apiRequest<unknown>('/match-rate/ask', { method: 'POST', body: JSON.stringify(data) })
  },
  async listQuestions() {
    return apiRequest<unknown[]>('/match-rate/questions')
  },
  async loadQuestion(id: number) {
    return apiRequest<unknown>(`/match-rate/questions/${id}`)
  },
  async deleteQuestion(id: number) {
    return apiRequest<{ deleted: boolean }>(`/match-rate/questions/${id}`, { method: 'DELETE' })
  },
  async bulk(data: { country_codes: string[]; ruleset: string; sort_priority?: string; address_required?: boolean; prefer_credit_header?: boolean }) {
    return apiRequest<unknown[]>('/match-rate/bulk', { method: 'POST', body: JSON.stringify(data) })
  },
  async extractCountries(text: string) {
    return apiRequest<{ codes: string[] }>('/match-rate/extract-countries', { method: 'POST', body: JSON.stringify({ text }) })
  },
  async saveAssessment(data: { name: string; countries: string[]; ruleset: string; sort_priority?: string; address_required?: boolean; results?: unknown[] }) {
    return apiRequest<{ id: string; name: string }>('/match-rate/assessments', { method: 'POST', body: JSON.stringify(data) })
  },
  async loadAssessment(id: string) {
    return apiRequest<{ id: string; name: string; countries: string[]; ruleset: string; sort_priority: string; address_required: boolean; results: unknown[] | null; created_by: string | null; created_at: string }>(`/match-rate/assessments/${id}`)
  },
  async deleteAssessment(id: string) {
    return apiRequest<{ deleted: boolean }>(`/match-rate/assessments/${id}`, { method: 'DELETE' })
  },
  async cascadeDesign(data: { country_codes: string[]; ruleset: string; sort_priority?: string; address_required?: boolean; prefer_credit_header?: boolean; deal_id?: string | null }) {
    return apiRequest<unknown[]>('/match-rate/cascade-design', { method: 'POST', body: JSON.stringify(data) })
  },
  async saveCascadeDesign(data: { id?: string; name: string; deal_id?: string | null; deal_name?: string | null; deal_company?: string | null; countries: string[]; ruleset: string; sort_priority?: string; address_required?: boolean; results?: unknown[] }) {
    return apiRequest<{ id: string; name: string }>('/match-rate/cascade-designs', { method: 'POST', body: JSON.stringify(data) })
  },
  async listCascadeDesigns(dealId?: string) {
    const params: Record<string, string> = {}
    if (dealId) params.deal_id = dealId
    return apiRequest<unknown[]>('/match-rate/cascade-designs', { params })
  },
  async loadCascadeDesign(id: string) {
    return apiRequest<{ id: string; deal_id: string | null; name: string; countries: string[]; ruleset: string; sort_priority: string; address_required: boolean; results: unknown[] | null; created_by: string | null; created_at: string }>(`/match-rate/cascade-designs/${id}`)
  },
  async deleteCascadeDesign(id: string) {
    return apiRequest<{ deleted: boolean }>(`/match-rate/cascade-designs/${id}`, { method: 'DELETE' })
  },
  // Expert overrides
  async listExpertOverrides(countryCode?: string) {
    const params: Record<string, string> = {}
    if (countryCode) params.country_code = countryCode
    return apiRequest<unknown[]>('/match-rate/expert-overrides', { params })
  },
  async saveExpertOverride(data: Record<string, unknown>) {
    return apiRequest<unknown>('/match-rate/expert-overrides', { method: 'POST', body: JSON.stringify(data) })
  },
  async deleteExpertOverride(id: number) {
    return apiRequest<{ deleted: boolean }>(`/match-rate/expert-overrides/${id}`, { method: 'DELETE' })
  },

  // Cascade benchmarks
  async listBenchmarks(countryCode?: string) {
    const params: Record<string, string> = {}
    if (countryCode) params.country_code = countryCode
    return apiRequest<unknown[]>('/match-rate/benchmarks', { params })
  },
  async saveBenchmark(data: Record<string, unknown>) {
    return apiRequest<{ id: number; created?: boolean; updated?: boolean }>('/match-rate/benchmarks', { method: 'POST', body: JSON.stringify(data) })
  },
  async deleteBenchmark(id: number) {
    return apiRequest<{ deleted: boolean }>(`/match-rate/benchmarks/${id}`, { method: 'DELETE' })
  },

  // Source latency
  async listSourceLatency(countryCode?: string) {
    const params: Record<string, string> = {}
    if (countryCode) params.country_code = countryCode
    return apiRequest<unknown[]>('/match-rate/source-latency', { params })
  },
  async saveSourceLatency(data: Record<string, unknown>) {
    return apiRequest<{ saved: boolean }>('/match-rate/source-latency', { method: 'POST', body: JSON.stringify(data) })
  },
  async deleteSourceLatency(id: number) {
    return apiRequest<{ deleted: boolean }>(`/match-rate/source-latency/${id}`, { method: 'DELETE' })
  },
}

// =====================================================
// Demo Prep API
// =====================================================
export const releaseNotesApi = {
  async list(limit = 20) {
    return apiRequest<unknown[]>('/release-notes', { params: { limit: limit.toString() } })
  },
  async create(data: { version: string; title: string; body: string; created_by?: string; created_by_name?: string }) {
    return apiRequest<unknown>('/release-notes', { method: 'POST', body: JSON.stringify(data) })
  },
  async remove(id: number) {
    return apiRequest<{ deleted: boolean }>(`/release-notes/${id}`, { method: 'DELETE' })
  },
}

// hour_settings is intentionally typed as `unknown` here — the feature
// layer (src/features/settings/store.ts) owns the HourSettings shape and
// validates/normalises the payload after fetching. This keeps client.ts
// free of feature-layer types.
export const hourSettingsApi = {
  async get() {
    return apiRequest<{
      hourSettings: unknown | null
      updatedBy: string | null
      updatedAt: string | null
    }>('/app-settings/hour-settings')
  },
  async put(hourSettings: unknown) {
    return apiRequest<{ success: boolean; updatedBy: string; updatedAt: string }>(
      '/app-settings/hour-settings',
      { method: 'PUT', body: JSON.stringify({ hourSettings }) }
    )
  },
}

export const demoPrepApi = {
  async list(dealId?: number) {
    const params: Record<string, string> = {}
    if (dealId) params.deal_id = dealId.toString()
    return apiRequest<unknown[]>('/demo-prep', { params })
  },
  async get(id: number) {
    return apiRequest<unknown>(`/demo-prep/${id}`)
  },
  async create(data: { title?: string; deal_id?: number; raw_input?: string; prospect_industry?: string; created_by?: string; created_by_name?: string }) {
    return apiRequest<unknown>('/demo-prep', { method: 'POST', body: JSON.stringify(data) })
  },
  async update(id: number, data: Record<string, unknown>) {
    return apiRequest<unknown>(`/demo-prep/${id}`, { method: 'PUT', body: JSON.stringify(data) })
  },
  async remove(id: number) {
    return apiRequest<{ deleted: boolean }>(`/demo-prep/${id}`, { method: 'DELETE' })
  },
  async clarify(id: number) {
    return apiRequest<{ questions: string[] }>(`/demo-prep/${id}/clarify`, { method: 'POST' })
  },
  async generate(id: number, clarifyAnswers?: string) {
    return apiRequest<unknown>(`/demo-prep/${id}/generate`, {
      method: 'POST',
      body: JSON.stringify(clarifyAnswers ? { clarify_answers: clarifyAnswers } : {}),
    })
  },
}

// =====================================================
// Data Sources API
// =====================================================
export const dataSourcesApi = {
  async list() {
    return apiRequest<unknown[]>('/data-sources')
  },
  async get(slug: string) {
    return apiRequest<unknown>(`/data-sources/${slug}`)
  },
  async getVersions(slug: string) {
    return apiRequest<unknown[]>(`/data-sources/${slug}/versions`)
  },
  async uploadVersion(slug: string, data: {
    version_label: string
    filename?: string
    file_size_bytes?: number
    file_data?: string
    row_count?: number
    checksum?: string
    uploaded_by?: string
    uploaded_by_name?: string
    notes?: string
  }) {
    return apiRequest<{ id: number }>(`/data-sources/${slug}/versions`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },
  async create(data: {
    slug: string
    display_name: string
    description?: string
    owner?: string
    category?: string
    review_frequency_days?: number
  }) {
    return apiRequest<{ id: number }>('/data-sources', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },
  async update(slug: string, data: Record<string, unknown>) {
    return apiRequest<{ updated: boolean }>(`/data-sources/${slug}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },
}

// =====================================================
// RFP Tool API
// =====================================================
export const rfpApi = {
  async listProjects() {
    return apiRequest<unknown[]>('/rfp')
  },
  async getAiPerformance() {
    return apiRequest<{ projects: any[]; reviewers: any[]; edited_samples: any[] }>('/rfp/ai-performance')
  },
  async analyzeEdits(sampleIds?: number[]) {
    return apiRequest<any>('/rfp/ai-performance/analyze', { method: 'POST', body: JSON.stringify({ sample_ids: sampleIds }) })
  },
  async getProject(id: number) {
    return apiRequest<unknown>(`/rfp/${id}`)
  },
  async createProject(data: { title?: string; deal_id?: number; original_filename?: string; created_by?: string; created_by_name?: string }) {
    return apiRequest<unknown>('/rfp', { method: 'POST', body: JSON.stringify(data) })
  },
  async updateProject(id: number, data: Record<string, unknown>) {
    return apiRequest<unknown>(`/rfp/${id}`, { method: 'PUT', body: JSON.stringify(data) })
  },
  async deleteProject(id: number) {
    return apiRequest<{ deleted: boolean }>(`/rfp/${id}`, { method: 'DELETE' })
  },
  async importRequirements(projectId: number, requirements: Array<{ row_index: number; sheet_name?: string; section?: string; requirement_name?: string; requirement_text: string; requirement_level?: string; original_response?: string }>) {
    return apiRequest<{ imported: number }>(`/rfp/${projectId}/requirements/import`, {
      method: 'POST',
      body: JSON.stringify({ requirements }),
    })
  },
  async listRequirements(projectId: number) {
    return apiRequest<unknown[]>(`/rfp/${projectId}/requirements`)
  },
  async updateRequirement(reqId: number, data: Record<string, unknown>) {
    return apiRequest<unknown>(`/rfp/requirements/${reqId}`, { method: 'PUT', body: JSON.stringify(data) })
  },
  async aiSearch(reqId: number) {
    return apiRequest<{ ai_response: string; fit: string; source: string }>(`/rfp/requirements/${reqId}/ai-search`, { method: 'POST' })
  },
  async reprocessRequirement(reqId: number, feedback?: string) {
    return apiRequest<{ ai_response: string; fit: string; source: string }>(`/rfp/requirements/${reqId}/reprocess`, { method: 'POST', body: JSON.stringify({ feedback }) })
  },
  async aiSearchBatch(projectId: number) {
    return apiRequest<{ batch_processed: number; batch_errors: number; total: number; processed: number; remaining: number; pass: number }>(`/rfp/projects/${projectId}/ai-search-batch`, { method: 'POST' })
  },
  async aiSearchBatchKb(projectId: number) {
    return apiRequest<{ batch_processed: number; batch_errors: number; total: number; processed: number; remaining: number; pass: number }>(`/rfp/projects/${projectId}/ai-search-batch-kb`, { method: 'POST' })
  },
  async aiSearchBatchKbLoose(projectId: number) {
    return apiRequest<{ batch_processed: number; batch_errors: number; total: number; processed: number; remaining: number; pass: number }>(`/rfp/projects/${projectId}/ai-search-batch-kb-loose`, { method: 'POST' })
  },
  async aiSearchBatchBrain(projectId: number) {
    return apiRequest<{ batch_processed: number; batch_errors: number; total: number; processed: number; remaining: number; pass: string }>(`/rfp/projects/${projectId}/ai-search-batch-brain`, { method: 'POST' })
  },
  async aiSearchBatchBrainReview(projectId: number) {
    return apiRequest<{ batch_processed: number; batch_errors: number; total: number; processed: number; remaining: number; pass: string }>(`/rfp/projects/${projectId}/ai-search-batch-brain-review`, { method: 'POST' })
  },
  async consolidateBatch(projectId: number) {
    return apiRequest<{ batch_processed: number; batch_errors: number; total: number; processed: number; remaining: number; pass: string }>(`/rfp/projects/${projectId}/consolidate-batch`, { method: 'POST' })
  },
  async resetConsolidateResponses(projectId: number) {
    return apiRequest<{ reset: boolean }>(`/rfp/projects/${projectId}/reset-consolidate`, { method: 'POST' })
  },
  async runOnServer(projectId: number, passes?: string[]) {
    return apiRequest<{ started: boolean; passes: string[]; project_title: string }>(`/rfp/projects/${projectId}/run-server`, { method: 'POST', ...(passes ? { body: JSON.stringify({ passes }) } : {}) })
  },
  async decomposeBatch(projectId: number, options?: { rerun?: boolean }) {
    return apiRequest<{ batch_processed: number; batch_errors: number; total: number; processed: number; remaining: number; restarted?: boolean }>(`/rfp/projects/${projectId}/decompose-batch`, { method: 'POST', ...(options?.rerun ? { body: JSON.stringify({ rerun: true }) } : {}) })
  },
  async getPassStatus(projectId: number) {
    return apiRequest<{ pass: string; last_run: string; log_count: number }[]>(`/rfp/projects/${projectId}/pass-status`)
  },
  async getProgress(projectId: number) {
    return apiRequest<{
      status: string
      decompose: { done: number; total: number } | null
      kb: { done: number; total: number }
      brain: { done: number; total: number } | null
      brain_review: { done: number; total: number } | null
      consolidate: { done: number; total: number } | null
    }>(`/rfp/projects/${projectId}/progress`)
  },
  async resetRequirements(projectId: number) {
    return apiRequest<{ reset: boolean }>(`/rfp/projects/${projectId}/reset`, { method: 'POST' })
  },
  async resetBrainResponses(projectId: number) {
    return apiRequest<{ reset: boolean }>(`/rfp/projects/${projectId}/reset-brain`, { method: 'POST' })
  },
  async resetBrainReviewResponses(projectId: number) {
    return apiRequest<{ reset: boolean }>(`/rfp/projects/${projectId}/reset-brain-review`, { method: 'POST' })
  },
  async deleteRequirements(projectId: number) {
    return apiRequest<{ deleted: boolean }>(`/rfp/projects/${projectId}/requirements`, { method: 'DELETE' })
  },
  async exportToSheets(projectId: number) {
    return apiRequest<{ sheet_title: string; sheet_url: string; row_count: number }>(`/rfp/projects/${projectId}/export-sheets`, { method: 'POST' })
  },
  async getLogs(projectId: number, limit = 200) {
    return apiRequest<Array<{ id: number; project_id: number; requirement_id: number | null; level: string; pass: string | null; provider: string | null; message: string; duration_ms: number | null; created_at: string }>>(`/rfp/projects/${projectId}/logs?limit=${limit}`)
  },
  async clearLogs(projectId: number) {
    return apiRequest<{ cleared: boolean }>(`/rfp/projects/${projectId}/logs`, { method: 'DELETE' })
  },
  async syncFromSheets(projectId: number) {
    return apiRequest<{ updated: number; total_rows: number }>(`/rfp/projects/${projectId}/sync-sheets`, { method: 'POST' })
  },
  async listGlobalReferences() {
    return apiRequest<unknown[]>('/rfp/references')
  },
  async uploadGlobalReference(data: { title: string; original_filename?: string; content_text: string; row_count?: number; created_by?: string; created_by_name?: string }) {
    return apiRequest<{ id: number }>('/rfp/references', { method: 'POST', body: JSON.stringify(data) })
  },
  async importQaPairs(refId: number, pairs: Array<{ row_index: number; section?: string; question: string; answer: string }>) {
    return apiRequest<{ imported: number }>(`/rfp/references/${refId}/qa-import`, {
      method: 'POST',
      body: JSON.stringify({ pairs }),
    })
  },
  async listQaPairs(refId: number) {
    return apiRequest<unknown[]>(`/rfp/references/${refId}/qa`)
  },
  async getReferenceContent(refId: number) {
    return apiRequest<{ content_text: string }>(`/rfp/references/${refId}/content`)
  },
  async generateTags(refId: number) {
    return apiRequest<{ started: boolean; total: number }>(`/rfp/references/${refId}/generate-tags`, { method: 'POST' })
  },
  async tagBatch(refId: number) {
    return apiRequest<{ batch_tagged: number; batch_errors: number; total_tagged: number; total: number; remaining: number }>(`/rfp/references/${refId}/tag-batch`, { method: 'POST' })
  },
  async tagProgress(refId: number) {
    return apiRequest<{ total: number; tagged: number; errors: number; remaining: number; status: string }>(`/rfp/references/${refId}/tag-progress`)
  },
  async listReferences(projectId: number) {
    return apiRequest<unknown[]>(`/rfp/${projectId}/references`)
  },
  async deleteReference(refId: number) {
    return apiRequest<{ deleted: boolean }>(`/rfp/references/${refId}`, { method: 'DELETE' })
  },
  async listTeam() {
    return apiRequest<unknown[]>('/rfp/team')
  },
  async addTeamMember(data: { name: string; email: string; expertise_areas?: string[]; description?: string }) {
    return apiRequest<{ id: number }>('/rfp/team', { method: 'POST', body: JSON.stringify(data) })
  },
  async updateTeamMember(id: number, data: { name?: string; email?: string; expertise_areas?: string[]; description?: string }) {
    return apiRequest<{ id: number }>(`/rfp/team/${id}`, { method: 'PUT', body: JSON.stringify(data) })
  },
  async deleteTeamMember(id: number) {
    return apiRequest<{ deleted: boolean }>(`/rfp/team/${id}`, { method: 'DELETE' })
  },
  // Project documents (supplementary RFP content)
  async listProjectDocuments(projectId: number) {
    return apiRequest<Array<{ id: number; project_id: number; title: string; content_length: number; created_by_name: string | null; created_at: string }>>(`/rfp/${projectId}/documents`)
  },
  async addProjectDocument(projectId: number, data: { title: string; content_text: string; created_by?: string; created_by_name?: string }) {
    return apiRequest<{ id: number }>(`/rfp/${projectId}/documents`, { method: 'POST', body: JSON.stringify(data) })
  },
  async getProjectDocument(docId: number) {
    return apiRequest<{ id: number; title: string; content_text: string }>(`/rfp/documents/${docId}`)
  },
  async deleteProjectDocument(docId: number) {
    return apiRequest<{ deleted: boolean }>(`/rfp/documents/${docId}`, { method: 'DELETE' })
  },
  // Default questions prompt
  async getDefaultQuestionsPrompt() {
    return apiRequest<{ prompt: string }>('/rfp/default-questions-prompt')
  },
  // AI qualification questions
  async generateQualificationQuestions(projectId: number, notes?: string) {
    return apiRequest<{ summary: string; groups: Array<{ theme: string; questions: Array<{ question: string; why: string; ask_who: string }> }> }>(
      `/rfp/${projectId}/qualification-questions`,
      { method: 'POST', body: JSON.stringify({ notes }) }
    )
  },
  async saveQuestions(projectId: number, questions: unknown) {
    return apiRequest<void>(
      `/rfp/${projectId}/questions`,
      { method: 'PUT', body: JSON.stringify({ questions }) }
    )
  },
  async aiAssist(projectId: number, data: { notes?: string; transcript_ids?: number[] }) {
    return apiRequest<{ suggestions: Record<string, { value: string | null; confidence: string; source: string }> }>(
      `/rfp/${projectId}/ai-assist`,
      { method: 'POST', body: JSON.stringify(data) }
    )
  },
  // Per-project team
  async listProjectTeam(projectId: number) {
    return apiRequest<unknown[]>(`/rfp/${projectId}/team`)
  },
  async addProjectTeamMember(projectId: number, data: { role: string; name: string; email: string }) {
    return apiRequest<{ id: number }>(`/rfp/${projectId}/team`, { method: 'POST', body: JSON.stringify(data) })
  },
  async deleteProjectTeamMember(id: number) {
    return apiRequest<{ deleted: boolean }>(`/rfp/project-team/${id}`, { method: 'DELETE' })
  },
  // Default routing
  async listRouting() {
    return apiRequest<unknown[]>('/rfp/routing')
  },
  async upsertRouting(data: { category: string; name: string; email: string }) {
    return apiRequest<unknown>('/rfp/routing', { method: 'PUT', body: JSON.stringify(data) })
  },
  async deleteRouting(category: string) {
    return apiRequest<{ deleted: boolean }>(`/rfp/routing/${encodeURIComponent(category)}`, { method: 'DELETE' })
  },
  async startReminders(projectId: number) {
    return apiRequest<{ action: string; message_posted: boolean }>(`/rfp/projects/${projectId}/reminders`, {
      method: 'POST',
      body: JSON.stringify({ action: 'start' }),
    })
  },
  async stopReminders(projectId: number) {
    return apiRequest<{ action: string }>(`/rfp/projects/${projectId}/reminders`, {
      method: 'POST',
      body: JSON.stringify({ action: 'stop' }),
    })
  },
  async getSlackMessages(projectId: number) {
    return apiRequest<Array<{ id: number; project_id: number; message_type: string; slack_ts: string | null; channel_id: string; message_text: string; people_tagged: string | null; pending_counts: string | null; created_at: string }>>(`/rfp/projects/${projectId}/slack-messages`)
  },
  async resendSlackMessage(messageId: number) {
    return apiRequest<{ resent: boolean; slack_ts: string | null }>(`/rfp/slack-messages/${messageId}/resend`, { method: 'POST' })
  },
  async sendCelebration(projectId: number) {
    return apiRequest<{ message_sent: boolean }>(`/rfp/projects/${projectId}/celebrate`, { method: 'POST' })
  },
  async postDetails(projectId: number) {
    return apiRequest<{ posted: boolean; slack_error?: string }>(`/rfp/projects/${projectId}/post-details`, { method: 'POST' })
  },
  async postReminderNow(projectId: number) {
    return apiRequest<{ project_id: number; title: string; action: string; message_sent: boolean }>(`/rfp/projects/${projectId}/reminders/test`, { method: 'POST' })
  },
}

const implTodoApi = {
  async getAll(context: string) {
    return apiRequest<ImplTodoCategory[]>(`/impl-todo?context=${encodeURIComponent(context)}`)
  },
  async createCategory(title: string, context: string) {
    return apiRequest<ImplTodoCategory>('/impl-todo/categories', {
      method: 'POST',
      body: JSON.stringify({ title, context }),
    })
  },
  async deleteCategory(id: number) {
    return apiRequest<{ deleted: boolean }>(`/impl-todo/categories/${id}`, { method: 'DELETE' })
  },
  async renameCategory(id: number, title: string) {
    return apiRequest<{ updated: boolean }>(`/impl-todo/categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    })
  },
  async createItem(data: { category_id: number; title: string; detail?: string; target_date?: string; status?: string }) {
    return apiRequest<ImplTodoItem>('/impl-todo/items', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },
  async updateItem(id: number, data: { status?: string; title?: string }) {
    return apiRequest<{ updated: boolean }>(`/impl-todo/items/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  },
  async deleteItem(id: number) {
    return apiRequest<{ deleted: boolean }>(`/impl-todo/items/${id}`, { method: 'DELETE' })
  },
}

// =====================================================
// IMPL TIME TRACKING API
// =====================================================
const implTimeApi = {
  async getSummary(): Promise<ApiResponse<{ summary: Record<string, { totalHours: number; entryCount: number }> }>> {
    return apiRequest('/impl-time/summary')
  },

  async getEntries(jiraKey: string): Promise<ApiResponse<{ entries: ImplTimeEntry[] }>> {
    return apiRequest(`/impl-time/entries`, { params: { jira_key: jiraKey } })
  },

  async createEntry(entry: {
    jira_key: string
    hours: number
    source: ImplTimeEntry['source']
    description?: string
    entry_date: string
  }): Promise<ApiResponse<{ id: number }>> {
    return apiRequest('/impl-time/entries', {
      method: 'POST',
      body: JSON.stringify(entry),
    })
  },

  async deleteEntry(id: number): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest(`/impl-time/entries/${id}`, { method: 'DELETE' })
  },

  async getJiraWorklogs(jiraKey: string): Promise<ApiResponse<{ worklogs: JiraWorklogEntry[] }>> {
    return apiRequest(`/jira/worklogs/${encodeURIComponent(jiraKey)}`)
  },

  async slaScan(): Promise<ApiResponse<SlaScanResult>> {
    return apiRequest('/impl-time/sla-preview')
  },

  async importSla(scanData: SlaScanResult): Promise<ApiResponse<{
    imported: { initiatives: number; entries: number; totalHours: number }
    scanned: SlaScanResult['scanned']
  }>> {
    return apiRequest('/impl-time/import-sla', { method: 'POST', body: JSON.stringify(scanData) })
  },

  async getLastImport(): Promise<ApiResponse<{ importedAt: string | null }>> {
    return apiRequest('/impl-time/last-import')
  },
}

export interface ImplDealMetadata {
  dealValue: number | null
  sowValue: number | null
  dealType: 'new' | 'upsell' | 'resell' | null
  gLink: string | null
  matchedCustomer: string | null
}

export const implDealMetaApi = {
  async lookup(issues: Array<{ key: string; summary: string }>): Promise<ApiResponse<{ metadata: Record<string, ImplDealMetadata> }>> {
    return apiRequest('/impl-deal-metadata/lookup', {
      method: 'POST',
      body: JSON.stringify({ issues }),
    })
  },
  async extractSow(jiraKey: string, pdfBase64: string): Promise<ApiResponse<{ sowValue: number | null }>> {
    return apiRequest('/impl-deal-metadata/extract-sow', {
      method: 'POST',
      body: JSON.stringify({ jiraKey, pdfBase64 }),
    })
  },
  async saveDealValue(jiraKey: string, dealValue: number | null): Promise<ApiResponse<void>> {
    return apiRequest('/impl-deal-metadata/deal-value', {
      method: 'PATCH',
      body: JSON.stringify({ jiraKey, dealValue }),
    })
  },
}

// ─── Vendor Registration (due-diligence emails) ────
export const vendorRegistrationApi = {
  async sendEmail(data: {
    to: string[]
    cc?: string[]
    subject: string
    html: string
    attachments?: Array<{ filename: string; content: string }>
  }): Promise<ApiResponse<{ id?: string }>> {
    return apiRequest<{ id?: string }>('/vendor-registration/send', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },
}

// ─── Cascade Builder AI ────────────────────────────
export const cascadeApi = {
  async aiGenerate(params: {
    country: string
    countryCode: string
    dealId?: number | null
    blueprintContext?: {
      businessModel?: string
      monthlyOnboardingVolume?: string
      strategicPriorities?: string
      verificationStack?: Array<{ layer: string; vendor: string; painPoints: string }>
      integrationMethod?: string
      jurisdictionsInScope?: string
      onboardingJourney?: string
      checksBeforeTransact?: string
      stpRate?: string
      fraudRate?: string
    }
    userNotes?: string
    includeHubspot?: boolean
    additionalInstructions?: string
  }): Promise<ApiResponse<{ nodes: unknown[]; edges: unknown[]; notes: string }>> {
    return apiRequest('/cascade/ai-generate', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  },

  async getProjects(dealId: number): Promise<ApiResponse<Array<{
    id: string; dealId: number; dealName: string
    cascades: Array<{ country: string; countryCode: string; nodes: unknown[]; edges: unknown[]; notes: string }>
    updatedAt: string
  }>>> {
    return apiRequest(`/cascade/projects?dealId=${dealId}`)
  },

  async saveProject(project: {
    id: string; dealId: number; dealName: string
    cascades: Array<{ country: string; countryCode: string; nodes: unknown[]; edges: unknown[]; notes: string }>
  }): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest('/cascade/projects', {
      method: 'PUT',
      body: JSON.stringify(project),
    })
  },

  async deleteProject(id: string): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest(`/cascade/projects/${id}`, { method: 'DELETE' })
  },

  async aiGenerateMulti(params: {
    countries: Array<{ country: string; countryCode: string }>
    dealId?: number | null
    blueprintContext?: {
      businessModel?: string
      monthlyOnboardingVolume?: string
      strategicPriorities?: string
      verificationStack?: Array<{ layer: string; vendor: string; painPoints: string }>
      integrationMethod?: string
      jurisdictionsInScope?: string
      onboardingJourney?: string
      checksBeforeTransact?: string
      stpRate?: string
      fraudRate?: string
    }
    userNotes?: string
    includeHubspot?: boolean
    additionalInstructions?: string
  }): Promise<ApiResponse<{
    cascades: Array<{ country: string; countryCode: string; nodes: unknown[]; edges: unknown[]; notes: string }>
    errors: Array<{ country: string; countryCode: string; error: string }>
  }>> {
    return apiRequest('/cascade/ai-generate-multi', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  },
}

// =====================================================
// Export all APIs
// =====================================================
export const api = {
  deals: dealsApi,
  transcripts: transcriptsApi,
  quotes: quotesApi,
  scopes: scopesApi,
  roi: roiApi,
  deliverables: deliverablesApi,
  products: productsApi,
  targetAccounts: targetAccountsApi,
  hubspot: hubspotApi,
  preferences: preferencesApi,
  schemaOverrides: schemaOverridesApi,
  accountPrompts: accountPromptsApi,
  feedback: feedbackApi,
  recipes: recipesApi,
  drafts: draftsApi,
  presalesDashboard: presalesDashboardApi,
  slack: slackApi,
  productManagers: productManagersApi,
  teamMembers: teamMembersApi,
  jira: jiraApi,
  implBot: implBotApi,
  helpDocTemplates: helpDocTemplatesApi,
  sowTemplate: sowTemplateApi,
  sequenceDiagramLibrary: sequenceDiagramLibraryApi,
  clientProjects: clientProjectsApi,
  platformJourneys: platformJourneysApi,
  platformJourneyAi: platformJourneyAiApi,
  poc: pocApi,
  pipelineProducts: pipelineProductsApi,
  dataSources: dataSourcesApi,
  countryGuides: countryGuidesApi,
  vendorApiDocs: vendorApiDocsApi,
  matchRate: matchRateApi,
  releaseNotes: releaseNotesApi,
  demoPrep: demoPrepApi,
  coverage: coverageApi,
  rfp: rfpApi,
  implTodo: implTodoApi,
  implTime: implTimeApi,
  implDealMeta: implDealMetaApi,
  cascade: cascadeApi,
}
export { pipelineProductsApi }

// =====================================================
// SANDBOX SEED API
// =====================================================
export const sandboxSeedApi = {
  async triggerSeed(): Promise<ApiResponse<{ message: string }>> {
    return apiRequest<{ message: string }>('/sandbox-seed', { method: 'POST' })
  },
  async triggerSeedV2(): Promise<ApiResponse<{ message: string }>> {
    return apiRequest<{ message: string }>('/sandbox-seed-v2', { method: 'POST' })
  },
  async triggerSeedV1(): Promise<ApiResponse<{ message: string }>> {
    return apiRequest<{ message: string }>('/sandbox-seed-v1', { method: 'POST' })
  },
}

// Also export individual APIs for convenience
export { apiRequest }
export default api
